import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card, Tabs, Tab, Alert, InputGroup, ButtonGroup, Accordion } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { provisioningApi, extensionsApi, systemApi, phonebookApi, queuesApi, nightModeApi, parkingApi, recordingsApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'
import BarcodeScanner from '../components/common/BarcodeScanner'

interface BLFKey {
  position: number
  type: string
  label: string
  value: string
  line: number
  pickup_code: string
}

interface CallQueue {
  id: string
  name: string
  display_name: string | null
}

interface NightModeRule {
  id: string
  name: string
  feature_code: string | null
  is_enabled: boolean
}

interface Template {
  id: string
  name: string
  description: string | null
  model: string
  model_info: { name: string; line_keys: number; accounts: number }
  blf_keys: BLFKey[]
  language: string
  timezone: string
  ringtone: string
  volume: number
  is_active: boolean
  assignment_count: number
}

interface DectAccount {
  account: number
  extension_id: string | null
  handset_name: string | null
}

interface Assignment {
  id: string
  mac_address: string
  template_id: string
  template_name: string
  model: string
  extension: { id: string; extension: string; name: string } | null
  description: string | null
  is_active: boolean
  last_provisioned: string | null
  // Encryption status
  encryption_active: boolean
  key_provisioned: boolean
  // RPS status
  rps_registered: boolean
  rps_registered_at: string | null
  rps_last_error: string | null
  rps_eol: boolean
  // DECT
  is_dect: boolean
  dect_accounts: DectAccount[]
}

interface PhoneModel {
  id: string
  name: string
  line_keys: number
  accounts: number
  is_dect?: boolean
  max_handsets?: number
}

interface Extension {
  id: string
  extension: string
  name: string
  voicemail_enabled?: boolean
  voicemail_email?: string
}

interface BLFType {
  value: string
  label: string
  description: string
}

interface SoftphoneType {
  id: string
  name: string
  description: string
  platforms: string[]
}

interface QrCodeData {
  qr_code: string
  qr_content: string
  extension: string
  name: string
  softphone: string
  softphone_name: string
  config: {
    display_name: string
    username: string
    password: string
    domain: string
    transport: string
    port: number
    provisioning_url?: string
    includes_ldap?: boolean
  }
}

interface RPSStatusInfo {
  mac_address: string
  local_registered: boolean
  local_registered_at: string | null
  local_error: string | null
  yealink: {
    registered: boolean
    source: string
    message?: string
    error?: string
    mac?: string
    server_url?: string
    model?: string
    created_at?: string
    updated_at?: string
  }
}

// Ringtone type for API response
interface Ringtone {
  name: string
  display_name: string
  type: 'builtin' | 'custom'
  url: string | null
}

const languages = [
  { value: 'German', label: 'Deutsch' },
  { value: 'English', label: 'English' },
  { value: 'French', label: 'Français' },
]
const timezones = [
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Vienna', label: 'Wien (CET/CEST)' },
  { value: 'Europe/Zurich', label: 'Zürich (CET/CEST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
]
const timeFormats = [
  { value: 0, label: '12-Stunden (AM/PM)' },
  { value: 1, label: '24-Stunden' },
]
const dateFormats = [
  { value: 0, label: 'WWW MMM DD (Mon Jan 01)' },
  { value: 1, label: 'DD-MMM-YY (01-Jan-25)' },
  { value: 2, label: 'YYYY-MM-DD (2025-01-01)' },
  { value: 3, label: 'DD/MM/YYYY (01/01/2025)' },
  { value: 4, label: 'MM/DD/YYYY (01/01/2025)' },
  { value: 5, label: 'DD MMM YYYY (01 Jan 2025)' },
  { value: 6, label: 'WWW DD MMM (Mon 01 Jan)' },
]

const emptyTemplateForm = {
  name: '',
  description: '',
  model: 'T46U',
  blf_keys: [] as BLFKey[],
  language: 'German',
  timezone: 'Europe/Berlin',
  ntp_server: 'pool.ntp.org',
  daylight_saving: true,
  time_format: 1,
  date_format: 0,
  ringtone: 'Ring1.wav',
  ringtone_internal: 'Ring1.wav',
  ringtone_external: 'Ring2.wav',
  ringtone_tapi: 'Ring3.wav',
  volume: 8,
  admin_password: 'admin',
  user_password: 'user',
  preserve_caller_id_on_transfer: true,
  preserve_caller_id_on_forward: true,
  connected_line_update: true,
  call_waiting: true,
  auto_answer: false,
  // CTI-Fernsteuerung (Softphone steuert das Tischtelefon)
  cti_auto_answer: false,
  cti_auto_answer_tone: true,
  sip_trust_server_only: false,
  allow_ip_call: false,
  cti_register_mac: false,
  // DSS Key settings
  first_key_as_line: true,
  dss_key_width: 0,  // 0=Standard, 1=Extended
  // Display/Layout settings
  wallpaper_url: '',
  wallpaper_file: null as string | null,
  screen_saver_type: 0,
  screen_saver_wait: 300,
  backlight_time: 30,
  contrast: 6,
  // MWI settings
  mwi_enabled: true,
  mwi_mailbox: '',
  // Power LED settings
  power_led_ringing: true,
  power_led_mwi: true,
  power_led_talking: false,
  power_led_missed_call: true,
  // LDAP settings
  ldap_enabled: true,
  ldap_lookup_incoming: true,
  ldap_lookup_outgoing: true,
  ldap_lookup_history: true,
  // Firmware settings
  auto_firmware_update: false,
  // Auto Provisioning settings
  auto_provision_enabled: false,
  auto_provision_interval: 1440,
  auto_provision_time: '',
  custom_config: `## Erweiterte Yealink-Konfiguration
## Diese Parameter können bearbeitet werden um die Standard-Einstellungen zu überschreiben.
## Hinweis: Auto-Provisionierung und Firmware-Updates werden im Tab "Sicherheit & Updates" konfiguriert.

##################################################
## Provisionierung
##################################################
## AES-Verschlüsselung für Config-Dateien
auto_provision.aes_key_in_file = 1
## Lokale Änderungen bei Provisionierung überschreiben (0=ja)
static.auto_provision.custom.protect = 0
## Neustart bei SIP NOTIFY (0=nein, 1=ja)
sip.notify_reboot_enable = 0

##################################################
## Zeit & NTP
##################################################
## Backup NTP-Server
local_time.ntp_server2 = time.google.com
## NTP-Sync Intervall in Sekunden
local_time.interval = 1000
## Sommerzeit-Typ (1=wochenbasiert für EU)
local_time.dst_time_type = 1

##################################################
## SIP-Registrierung
##################################################
## SIP-Port
account.1.sip_server.1.port = 5060
## Registrierungs-Intervall in Sekunden
account.1.sip_server.1.expires = 3600
## NAT Keep-Alive Intervall
account.1.nat.udp_update_time = 20

##################################################
## Voicemail/MWI
##################################################
## Stotter-Wählton bei neuen Nachrichten
features.voice_mail_tone_enable = 1
## Popup bei neuer Voicemail
features.voice_mail_popup.enable = 1
## LED blinkt bei Voicemail
phone_setting.mwi_led.power_led_flash_enable = 1

##################################################
## Transfer & Pickup
##################################################
## Semi-Attended Transfer aktivieren
transfer.semi_attend_tran_enable = 1
## Blind Transfer bei Auflegen
transfer.blind_tran_on_hook_enable = 1
## Transfer bei Auflegen aktivieren
transfer.on_hook_trans_enable = 1
## Gerichteter Pickup Code (z.B. **101)
features.pickup.direct_pickup_code = **
## Gruppen-Pickup Code
features.pickup.group_pickup_code = *8

##################################################
## DND (Nicht Stören)
##################################################
## DND aktivieren Code
features.dnd.on_code = *78
## DND deaktivieren Code
features.dnd.off_code = *79

##################################################
## NAT & STUN
##################################################
## NAT Traversal aktivieren
account.1.nat.nat_traversal = 1
## STUN-Server (Google Public)
account.1.nat.stun_server = stun.l.google.com:19302
## ICE aktivieren
account.1.nat.ice_enable = 1

##################################################
## BLF-Einstellungen
##################################################
## LED-Modus: 0=Standard (Grün=Frei, Rot=Besetzt, Aus=Unavailable)
features.blf_led_mode = 0
## Anrufer-ID bei BLF-Anruf anzeigen
features.blf.show_callid.enable = 1
## Visuelles BLF-Pickup
features.pickup.blf_visual_enable = 1
## Audio-BLF-Pickup (0=aus)
features.pickup.blf_audio_enable = 0

##################################################
## Sicherheit
##################################################
## Warnungen ausblenden
phone_setting.warnings_display.mode = 0
## Zertifikaten vertrauen
security.trust_certificates = 0
## Direkt-IP-Anrufe deaktivieren
feature.direct_ip_call_enable = 0
`
}

export default function Provisioning() {
  const confirm = useConfirm()
  const [activeTab, setActiveTab] = useState('templates')
  const [templates, setTemplates] = useState<Template[]>([])
  const [assignments, setAssignments] = useState<Assignment[]>([])
  // Live-Abgleich gegen das Yealink-Konto, Schlüssel = MAC ohne Trenner, lowercase.
  // Das Feld rps_registered der Zuweisung ist nur eine Momentaufnahme des letzten
  // erfolgreichen API-Aufrufs; ohne diesen Abgleich blieb das Badge auch dann grün,
  // wenn das Gerät längst nicht mehr in unserem RPS-Konto liegt.
  const [rpsLive, setRpsLive] = useState<Record<string, { state: string; server_url?: string | null; server_name?: string | null }>>({})
  const [rpsOrphans, setRpsOrphans] = useState<Array<{ mac: string; server_url?: string | null; server_name?: string | null }>>([])
  // Bereinigungs-Dialog
  const [showRpsFixModal, setShowRpsFixModal] = useState(false)
  const [rpsFixChecking, setRpsFixChecking] = useState(false)
  const [rpsFixRunning, setRpsFixRunning] = useState(false)
  const [rpsFixSelection, setRpsFixSelection] = useState<{ macs: string[]; orphans: string[] }>({ macs: [], orphans: [] })
  const [rpsFixResults, setRpsFixResults] = useState<Array<{ mac: string; action: string; success: boolean; error?: string | null }> | null>(null)
  const [models, setModels] = useState<PhoneModel[]>([])
  const [extensions, setExtensions] = useState<Extension[]>([])
  const [blfTypes, setBlfTypes] = useState<BLFType[]>([])
  const [queues, setQueues] = useState<CallQueue[]>([])
  const [nightModes, setNightModes] = useState<NightModeRule[]>([])
  const [parkingLots, setParkingLots] = useState<{ id: string; name: string; parking_extension: string; start_slot: number; end_slot: number }[]>([])
  const [recordings, setRecordings] = useState<{ id: string; name: string; dial_code: string | null }[]>([])
  const [ringtones, setRingtones] = useState<Ringtone[]>([])
  const [uploadingRingtone, setUploadingRingtone] = useState(false)
  const [loading, setLoading] = useState(true)

  // Template Modal
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null)
  const [templateForm, setTemplateForm] = useState(emptyTemplateForm)

  // Assignment Modal
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null)
  const [assignmentForm, setAssignmentForm] = useState({
    mac_address: '',
    template_id: '',
    extension_id: '',
    description: '',
    dect_accounts: [] as DectAccount[]
  })
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false)

  // Clone Template Modal
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [cloningTemplate, setCloningTemplate] = useState<Template | null>(null)
  const [cloneName, setCloneName] = useState('')

  // Helper: Check if selected template is DECT
  const isSelectedTemplateDect = (): boolean => {
    if (!assignmentForm.template_id) return false
    const template = templates.find(t => t.id === assignmentForm.template_id)
    if (!template) return false
    const dectModels = ['W60B', 'W70B', 'W73B', 'W80B', 'W80DM', 'W90B', 'W90DM']
    return dectModels.includes(template.model)
  }

  // Get max handsets for selected DECT template
  const getMaxHandsets = (): number => {
    if (!assignmentForm.template_id) return 0
    const template = templates.find(t => t.id === assignmentForm.template_id)
    if (!template) return 0
    const handsetLimits: Record<string, number> = {
      'W60B': 8, 'W70B': 10, 'W73B': 10, 'W80B': 100, 'W80DM': 100, 'W90B': 100, 'W90DM': 100
    }
    return handsetLimits[template.model] || 8
  }

  // Add DECT account
  const addDectAccount = () => {
    const nextAccount = assignmentForm.dect_accounts.length + 1
    if (nextAccount <= getMaxHandsets()) {
      setAssignmentForm({
        ...assignmentForm,
        dect_accounts: [
          ...assignmentForm.dect_accounts,
          { account: nextAccount, extension_id: null, handset_name: `Handset ${nextAccount}` }
        ]
      })
    }
  }

  // Remove DECT account
  const removeDectAccount = (index: number) => {
    const newAccounts = assignmentForm.dect_accounts.filter((_, i) => i !== index)
    // Re-number accounts
    const renumbered = newAccounts.map((acc, i) => ({ ...acc, account: i + 1 }))
    setAssignmentForm({ ...assignmentForm, dect_accounts: renumbered })
  }

  // Update DECT account
  const updateDectAccount = (index: number, field: keyof DectAccount, value: string | number | null) => {
    const newAccounts = [...assignmentForm.dect_accounts]
    newAccounts[index] = { ...newAccounts[index], [field]: value }
    setAssignmentForm({ ...assignmentForm, dect_accounts: newAccounts })
  }

  // Config Preview Modal
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewConfig, setPreviewConfig] = useState('')
  const [previewInfo, setPreviewInfo] = useState<{ mac?: string; template?: string; extension?: string }>({})

  // RPS Status Modal
  const [showRPSStatusModal, setShowRPSStatusModal] = useState(false)
  const [rpsStatusInfo, setRpsStatusInfo] = useState<RPSStatusInfo | null>(null)
  const [loadingRPSStatus, setLoadingRPSStatus] = useState(false)

  // Softphone QR Code state
  const [softphoneTypes, setSoftphoneTypes] = useState<SoftphoneType[]>([])
  const [selectedExtension, setSelectedExtension] = useState('')
  // Voreinstellung: unsere eigene App (AS 13.10.2026 — nur noch it64PbxPhone empfehlen)
  const [selectedSoftphone, setSelectedSoftphone] = useState('it64pbx')
  const [serverIp, setServerIp] = useState('')
  const [transport, setTransport] = useState('UDP')
  const [qrCodeData, setQrCodeData] = useState<QrCodeData | null>(null)
  const [generatingQr, setGeneratingQr] = useState(false)

  useEffect(() => {
    loadData()
    // Auto-detect server IP from current URL
    const currentHost = window.location.hostname
    if (currentHost && currentHost !== 'localhost') {
      setServerIp(currentHost)
    }
  }, [])

  const loadData = async () => {
    try {
      const [templatesRes, assignmentsRes, modelsRes, extRes, blfRes, softphoneRes, queuesRes, nightModesRes, ringtonesRes, parkingRes, recordingsRes] = await Promise.all([
        provisioningApi.listTemplates(),
        provisioningApi.listAssignments(),
        provisioningApi.listModels(),
        extensionsApi.list(),
        provisioningApi.listBlfTypes(),
        systemApi.getSoftphoneTypes(),
        queuesApi.list(),
        nightModeApi.list().catch(() => ({ data: { items: [] } })),  // Fallback if no night modes
        provisioningApi.listRingtones().catch(() => ({ data: { ringtones: [] } })),  // Load ringtones
        parkingApi.list().catch(() => ({ data: { items: [] } })),
        recordingsApi.list().catch(() => ({ data: { items: [] } }))
      ])
      setTemplates(templatesRes.data.items || [])
      setAssignments(assignmentsRes.data.items || [])
      setModels(modelsRes.data.models || [])
      setExtensions(extRes.data.items || [])
      setBlfTypes(blfRes.data.types || [])
      setSoftphoneTypes(softphoneRes.data.types || [])
      setQueues(queuesRes.data.items || [])
      setNightModes(nightModesRes.data.items || [])
      setRingtones(ringtonesRes.data.ringtones || [])
      setParkingLots(parkingRes.data.items || [])
      setRecordings(recordingsRes.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
    // Bewusst nicht awaited: der Abgleich ruft die Yealink-API und darf die Liste
    // nicht aufhalten. Solange er läuft, zeigen die Badges den DB-Stand.
    loadRpsLive()
  }

  const loadRpsLive = async () => {
    try {
      const res = await provisioningApi.reconcileRPS()
      setRpsLive(res.data?.available ? (res.data.devices || {}) : {})
      setRpsOrphans(res.data?.available ? (res.data.orphans || []) : [])
    } catch {
      // Kein Toast: RPS aus, keine Zugangsdaten oder Yealink nicht erreichbar ist
      // kein Grund, die Provisionierungsseite mit Fehlern zuzupflastern.
      setRpsLive({})
      setRpsOrphans([])
    }
  }

  const macKey = (mac: string) => mac.replace(/[:-]/g, '').toLowerCase()

  // MACs, die laut Abgleich nicht stimmen — Grundlage für Button-Zähler und Dialog
  const rpsProblemMacs = assignments
    .filter(a => a.rps_registered && ['missing', 'no_url'].includes(rpsLive[macKey(a.mac_address)]?.state))
    .map(a => a.mac_address)
  const rpsProblemCount = rpsProblemMacs.length + rpsOrphans.length

  const openRpsFixModal = async () => {
    setRpsFixResults(null)
    setShowRpsFixModal(true)
    setRpsFixChecking(true)
    // Vor dem Anzeigen frisch abgleichen: der Stand kann Minuten alt sein und
    // niemand soll auf Basis veralteter Daten Geräte umregistrieren.
    await loadRpsLive()
    setRpsFixChecking(false)
  }

  // Vorauswahl immer auf den aktuellen Abgleich setzen, solange nichts gelaufen ist
  useEffect(() => {
    if (showRpsFixModal && !rpsFixResults) {
      setRpsFixSelection({ macs: rpsProblemMacs, orphans: rpsOrphans.map(o => o.mac) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRpsFixModal, rpsLive, rpsOrphans])

  const runRpsFix = async () => {
    setRpsFixRunning(true)
    try {
      const res = await provisioningApi.fixRPS({
        macs: rpsFixSelection.macs,
        orphan_macs: rpsFixSelection.orphans,
      })
      setRpsFixResults(res.data.results || [])
      if (res.data.succeeded > 0) toast.success(`${res.data.succeeded} Einträge bereinigt`)
      if (res.data.failed > 0) toast.warning(`${res.data.failed} Einträge konnten nicht bereinigt werden`)
      await loadData()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Bereinigung fehlgeschlagen')
    } finally {
      setRpsFixRunning(false)
    }
  }

  const toggleFixMac = (mac: string) => {
    setRpsFixSelection(s => ({
      ...s,
      macs: s.macs.includes(mac) ? s.macs.filter(m => m !== mac) : [...s.macs, mac],
    }))
  }

  const toggleFixOrphan = (mac: string) => {
    setRpsFixSelection(s => ({
      ...s,
      orphans: s.orphans.includes(mac) ? s.orphans.filter(m => m !== mac) : [...s.orphans, mac],
    }))
  }

  // ============ RINGTONE FUNCTIONS ============

  const handleRingtoneUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.wav')) {
      toast.error('Nur WAV-Dateien sind erlaubt')
      return
    }

    // Validate file size (max 300KB for Yealink)
    if (file.size > 300 * 1024) {
      toast.error('Datei zu gross (max. 300KB)')
      return
    }

    setUploadingRingtone(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      await provisioningApi.uploadRingtone(formData)
      toast.success(`Klingelton "${file.name}" hochgeladen`)
      // Reload ringtones
      const res = await provisioningApi.listRingtones()
      setRingtones(res.data.ringtones || [])
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen')
    } finally {
      setUploadingRingtone(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const handleRingtoneDelete = async (filename: string) => {
    const confirmed = await confirm({
      title: 'Klingelton löschen',
      message: `Möchten Sie den Klingelton "${filename}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return

    try {
      await provisioningApi.deleteRingtone(filename)
      toast.success(`Klingelton "${filename}" gelöscht`)
      // Reload ringtones
      const res = await provisioningApi.listRingtones()
      setRingtones(res.data.ringtones || [])
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen')
    }
  }

  // ============ TEMPLATE FUNCTIONS ============

  const openTemplateModal = async (template?: Template) => {
    if (template) {
      setEditingTemplate(template)
      // Load full template details including LDAP settings
      try {
        const res = await provisioningApi.getTemplate(template.id)
        const t = res.data
        setTemplateForm({
          name: t.name,
          description: t.description || '',
          model: t.model,
          blf_keys: t.blf_keys || [],
          language: t.language,
          timezone: t.timezone,
          ntp_server: t.ntp_server || 'pool.ntp.org',
          daylight_saving: t.daylight_saving ?? true,
          time_format: t.time_format ?? 1,
          date_format: t.date_format ?? 0,
          ringtone: t.ringtone,
          ringtone_internal: t.ringtone_internal || 'Ring1.wav',
          ringtone_external: t.ringtone_external || 'Ring2.wav',
          ringtone_tapi: t.ringtone_tapi || 'Ring3.wav',
          volume: t.volume,
          admin_password: t.admin_password || 'admin',
          user_password: t.user_password || 'user',
          preserve_caller_id_on_transfer: t.preserve_caller_id_on_transfer ?? true,
          preserve_caller_id_on_forward: t.preserve_caller_id_on_forward ?? true,
          connected_line_update: t.connected_line_update ?? true,
          call_waiting: t.call_waiting ?? true,
          auto_answer: t.auto_answer ?? false,
          cti_auto_answer: t.cti_auto_answer ?? false,
          cti_auto_answer_tone: t.cti_auto_answer_tone ?? true,
          sip_trust_server_only: t.sip_trust_server_only ?? false,
          allow_ip_call: t.allow_ip_call ?? false,
          cti_register_mac: t.cti_register_mac ?? false,
          first_key_as_line: t.first_key_as_line ?? true,
          dss_key_width: t.dss_key_width ?? 1,
          // Display/Layout settings
          wallpaper_url: t.wallpaper_url || '',
          wallpaper_file: t.wallpaper_file || null,
          screen_saver_type: t.screen_saver_type ?? 0,
          screen_saver_wait: t.screen_saver_wait ?? 300,
          backlight_time: t.backlight_time ?? 30,
          contrast: t.contrast ?? 6,
          // MWI settings
          mwi_enabled: t.mwi_enabled ?? true,
          mwi_mailbox: t.mwi_mailbox || '',
          // Power LED settings
          power_led_ringing: t.power_led_ringing ?? true,
          power_led_mwi: t.power_led_mwi ?? true,
          power_led_talking: t.power_led_talking ?? false,
          power_led_missed_call: t.power_led_missed_call ?? true,
          // LDAP settings
          ldap_enabled: t.ldap_enabled ?? true,
          ldap_lookup_incoming: t.ldap_lookup_incoming ?? true,
          ldap_lookup_outgoing: t.ldap_lookup_outgoing ?? true,
          ldap_lookup_history: t.ldap_lookup_history ?? true,
          auto_firmware_update: t.auto_firmware_update ?? false,
          auto_provision_enabled: t.auto_provision_enabled ?? false,
          auto_provision_interval: t.auto_provision_interval ?? 1440,
          auto_provision_time: t.auto_provision_time || '',
          custom_config: t.custom_config || ''
        })
      } catch {
        toast.error('Fehler beim Laden der Vorlage')
        return
      }
    } else {
      setEditingTemplate(null)
      setTemplateForm(emptyTemplateForm)
    }
    setShowTemplateModal(true)
  }

  const handleTemplateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingTemplate) {
        await provisioningApi.updateTemplate(editingTemplate.id, templateForm)
        toast.success('Vorlage aktualisiert')
      } else {
        await provisioningApi.createTemplate(templateForm)
        toast.success('Vorlage erstellt')
      }
      setShowTemplateModal(false)
      loadData()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const deleteTemplate = async (template: Template) => {
    if (template.assignment_count > 0) {
      toast.error(`Vorlage hat noch ${template.assignment_count} MAC-Zuweisungen`)
      return
    }
    const confirmed = await confirm({
      title: 'Vorlage löschen',
      message: `Vorlage "${template.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await provisioningApi.deleteTemplate(template.id)
      toast.success('Vorlage gelöscht')
      loadData()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const openCloneModal = (template: Template) => {
    setCloningTemplate(template)
    setCloneName(`${template.name} (Kopie)`)
    setShowCloneModal(true)
  }

  const handleCloneTemplate = async () => {
    if (!cloningTemplate || !cloneName.trim()) return
    try {
      await provisioningApi.cloneTemplate(cloningTemplate.id, cloneName.trim())
      toast.success(`Vorlage "${cloningTemplate.name}" wurde geklont`)
      setShowCloneModal(false)
      setCloningTemplate(null)
      setCloneName('')
      loadData()
    } catch {
      toast.error('Fehler beim Klonen der Vorlage')
    }
  }

  // ============ ASSIGNMENT FUNCTIONS ============

  const openAssignmentModal = (assignment?: Assignment) => {
    if (assignment) {
      setEditingAssignment(assignment)
      setAssignmentForm({
        mac_address: assignment.mac_address,
        template_id: assignment.template_id,
        extension_id: assignment.extension?.id || '',
        description: assignment.description || '',
        dect_accounts: assignment.dect_accounts || []
      })
    } else {
      setEditingAssignment(null)
      setAssignmentForm({ mac_address: '', template_id: templates[0]?.id || '', extension_id: '', description: '', dect_accounts: [] })
    }
    setShowAssignmentModal(true)
  }

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingAssignment) {
        await provisioningApi.updateAssignment(editingAssignment.id, assignmentForm)
        toast.success('Zuweisung aktualisiert')
      } else {
        await provisioningApi.createAssignment(assignmentForm)
        toast.success('MAC-Adresse zugewiesen')
      }
      setShowAssignmentModal(false)
      loadData()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const deleteAssignment = async (assignment: Assignment) => {
    const confirmed = await confirm({
      title: 'MAC-Zuweisung entfernen',
      message: `MAC ${assignment.mac_address} wirklich entfernen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await provisioningApi.deleteAssignment(assignment.id)
      toast.success('Zuweisung gelöscht')
      loadData()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const resetEncryptionKey = async (assignment: Assignment) => {
    const confirmed = await confirm({
      title: 'Verschlüsselung zurücksetzen',
      message: `Verschlüsselungsschlüssel für ${assignment.mac_address} zurücksetzen?\n\nDie nächste Provisionierung erfolgt unverschlüsselt mit einem neuen Schlüssel. Das Telefon muss in einer kontrollierten Umgebung neu provisioniert werden.`,
      confirmText: 'Zurücksetzen',
      variant: 'warning'
    })
    if (!confirmed) return
    try {
      await provisioningApi.resetEncryption(assignment.id)
      toast.success('Verschlüsselungsschlüssel zurückgesetzt')
      loadData()
    } catch {
      toast.error('Fehler beim Zurücksetzen')
    }
  }

  const triggerReprovision = async (assignment: Assignment, reboot: boolean = false) => {
    if (!assignment.extension) {
      toast.error('Keine Nebenstelle zugewiesen - Re-Provisionierung nicht möglich')
      return
    }
    try {
      await provisioningApi.reprovision(assignment.id, reboot)
      toast.success(`Re-Provisionierung für ${assignment.mac_address} ausgelöst`)
    } catch {
      toast.error('Fehler bei Re-Provisionierung')
    }
  }

  // ============ YEALINK RPS ============

  const registerWithRPS = async (assignment: Assignment) => {
    try {
      await provisioningApi.registerRPS(assignment.id)
      toast.success(`MAC ${assignment.mac_address} bei Yealink RPS registriert`)
      loadData()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler bei RPS-Registrierung')
      loadData() // Refresh to show error status
    }
  }

  const resetRPSStatus = async (assignment: Assignment) => {
    try {
      await provisioningApi.resetRPS(assignment.id)
      toast.success('RPS-Status zurückgesetzt')
      loadData()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen')
    }
  }

  const unregisterFromRPS = async (assignment: Assignment) => {
    if (!window.confirm(`RPS-Registrierung für MAC ${assignment.mac_address} wirklich löschen?`)) {
      return
    }
    try {
      await provisioningApi.unregisterRPS(assignment.id)
      toast.success(`MAC ${assignment.mac_address} bei Yealink RPS abgemeldet`)
      loadData()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler bei RPS-Abmeldung')
    }
  }

  const checkRPSStatus = async (assignment: Assignment) => {
    setLoadingRPSStatus(true)
    setShowRPSStatusModal(true)
    setRpsStatusInfo(null)
    try {
      const status = await provisioningApi.getRPSStatusById(assignment.id)
      setRpsStatusInfo(status)
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler beim Abrufen des RPS-Status')
      setShowRPSStatusModal(false)
    } finally {
      setLoadingRPSStatus(false)
    }
  }

  const registerAllWithRPS = async () => {
    try {
      const result = await provisioningApi.registerAllRPS()
      if (result.registered > 0) {
        toast.success(`${result.registered} Telefone bei RPS registriert`)
      }
      if (result.failed > 0) {
        toast.warning(`${result.failed} Registrierungen fehlgeschlagen`)
      }
      loadData()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail || 'Fehler bei Bulk-Registrierung')
    }
  }

  // ============ CONFIG PREVIEW ============

  const showConfigPreview = async (assignment: Assignment) => {
    try {
      const res = await provisioningApi.previewConfig(assignment.id)
      setPreviewConfig(res.data.config)
      setPreviewInfo({
        mac: assignment.mac_address,
        template: res.data.template_name,
        extension: res.data.extension
      })
      setShowPreviewModal(true)
    } catch {
      toast.error('Fehler beim Laden der Vorschau')
    }
  }

  // ============ BLF KEY FUNCTIONS ============

  // State for drag and drop
  const [draggedBlfIndex, setDraggedBlfIndex] = useState<number | null>(null)
  // State for view mode (compact table vs detailed cards)
  const [blfViewMode, setBlfViewMode] = useState<'compact' | 'detail' | 'accordion'>('accordion')

  // Get BLF keys sorted by position
  const getSortedBlfKeys = () => {
    return [...templateForm.blf_keys].sort((a, b) => a.position - b.position)
  }

  const addBlfKey = () => {
    const selectedModel = models.find(m => m.id === templateForm.model)
    const maxKeys = selectedModel?.line_keys || 27
    if (templateForm.blf_keys.length >= maxKeys) {
      toast.warning(`Maximal ${maxKeys} Tasten für dieses Modell`)
      return
    }
    const nextPos = templateForm.blf_keys.length > 0
      ? Math.max(...templateForm.blf_keys.map(k => k.position)) + 1
      : 1
    setTemplateForm({
      ...templateForm,
      blf_keys: [...templateForm.blf_keys, { position: nextPos, type: 'blf', label: '', value: '', line: 1, pickup_code: '*8' }]
    })
  }

  const updateBlfKey = (position: number, field: string, value: string | number) => {
    setTemplateForm(prev => ({
      ...prev,
      blf_keys: prev.blf_keys.map(key =>
        key.position === position ? { ...key, [field]: value } : key
      )
    }))
  }

  // Update multiple fields at once to avoid state batching issues
  const updateBlfKeyMultiple = (position: number, updates: Partial<BLFKey>) => {
    setTemplateForm(prev => ({
      ...prev,
      blf_keys: prev.blf_keys.map(key =>
        key.position === position ? { ...key, ...updates } : key
      )
    }))
  }

  const removeBlfKey = (position: number) => {
    const newKeys = templateForm.blf_keys
      .filter(key => key.position !== position)
      .map(key => ({
        ...key,
        position: key.position > position ? key.position - 1 : key.position
      }))
    setTemplateForm({ ...templateForm, blf_keys: newKeys })
  }

  // Drag and Drop handlers for BLF keys
  const handleBlfDragStart = (e: React.DragEvent, position: number) => {
    setDraggedBlfIndex(position)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', position.toString())
    const target = e.target as HTMLElement
    setTimeout(() => target.style.opacity = '0.5', 0)
  }

  const handleBlfDragEnd = (e: React.DragEvent) => {
    setDraggedBlfIndex(null)
    const target = e.target as HTMLElement
    target.style.opacity = '1'
  }

  const handleBlfDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleBlfDrop = (e: React.DragEvent, targetPosition: number) => {
    e.preventDefault()
    if (draggedBlfIndex === null || draggedBlfIndex === targetPosition) return

    const draggedPosition = draggedBlfIndex

    // Swap positions between dragged and target
    const newKeys = templateForm.blf_keys.map(key => {
      if (key.position === draggedPosition) {
        return { ...key, position: targetPosition }
      }
      if (key.position === targetPosition) {
        return { ...key, position: draggedPosition }
      }
      return key
    })

    setTemplateForm({ ...templateForm, blf_keys: newKeys })
    setDraggedBlfIndex(null)
  }

  const moveBlfKey = (position: number, direction: 'up' | 'down') => {
    const sortedKeys = getSortedBlfKeys()
    const currentIndex = sortedKeys.findIndex(k => k.position === position)
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (targetIndex < 0 || targetIndex >= sortedKeys.length) return

    const targetPosition = sortedKeys[targetIndex].position

    // Swap positions
    const newKeys = templateForm.blf_keys.map(key => {
      if (key.position === position) {
        return { ...key, position: targetPosition }
      }
      if (key.position === targetPosition) {
        return { ...key, position: position }
      }
      return key
    })

    setTemplateForm({ ...templateForm, blf_keys: newKeys })
  }

  const getModelInfo = (modelId: string) => models.find(m => m.id === modelId)

  // ============ SOFTPHONE QR CODE FUNCTIONS ============

  const generateQrCode = async () => {
    if (!selectedExtension || !serverIp) {
      toast.error('Bitte Nebenstelle und Server-IP auswählen')
      return
    }

    setGeneratingQr(true)
    try {
      // For linphone_full and it64pbx, we need to pass the provisioning URL (current origin)
      const provisioningUrl = ['linphone_full', 'it64pbx'].includes(selectedSoftphone)
        ? window.location.origin
        : undefined

      const res = await systemApi.generateQrCode(
        selectedExtension,
        serverIp,
        transport,
        selectedSoftphone,
        provisioningUrl
      )
      setQrCodeData(res.data)
    } catch {
      toast.error('Fehler beim Generieren des QR-Codes')
    } finally {
      setGeneratingQr(false)
    }
  }

  const downloadQrCode = () => {
    if (!qrCodeData) return
    const link = document.createElement('a')
    link.href = qrCodeData.qr_code
    link.download = `${qrCodeData.softphone}-${qrCodeData.extension}.png`
    link.click()
  }

  const printQrCode = () => {
    if (!qrCodeData) return
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${qrCodeData.softphone_name} QR-Code - ${qrCodeData.name}</title>
            <style>
              body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
              img { max-width: 300px; margin: 20px 0; }
              h2 { margin-bottom: 10px; }
              .info { margin-top: 20px; text-align: left; max-width: 400px; margin-left: auto; margin-right: auto; }
              .info p { margin: 5px 0; }
              .label { font-weight: bold; }
            </style>
          </head>
          <body>
            <h2>${qrCodeData.softphone_name} Konfiguration</h2>
            <h3>${qrCodeData.name} (${qrCodeData.extension})</h3>
            <img src="${qrCodeData.qr_code}" alt="QR Code" />
            <div class="info">
              <p><span class="label">Server:</span> ${qrCodeData.config.domain}</p>
              <p><span class="label">Benutzername:</span> ${qrCodeData.config.username}</p>
              <p><span class="label">Transport:</span> ${qrCodeData.config.transport.toUpperCase()}</p>
              <p><span class="label">Port:</span> ${qrCodeData.config.port}</p>
            </div>
            <p style="margin-top: 30px; font-size: 12px; color: #666;">
              Scannen Sie diesen QR-Code mit der ${qrCodeData.softphone_name}-App, um das Konto automatisch zu konfigurieren.
            </p>
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.print()
    }
  }

  const sendQrCodeEmail = async () => {
    if (!qrCodeData || !selectedExtension) return
    const ext = extensions.find(e => e.id === selectedExtension)
    let email = ext?.voicemail_email || ''

    if (!email) {
      const input = prompt('Keine E-Mail an der Nebenstelle hinterlegt. Bitte E-Mail-Adresse eingeben:')
      if (!input) return
      email = input
    }

    try {
      const provisioningUrl = ['linphone_full', 'it64pbx', 'groundwire'].includes(selectedSoftphone)
        ? window.location.origin
        : undefined
      await systemApi.sendQrCodeEmail(selectedExtension, serverIp, transport, selectedSoftphone, provisioningUrl, email)
      toast.success(`QR-Code wurde an ${email} gesendet`)
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Fehler beim Senden der E-Mail')
    }
  }

  if (loading) return <div className="text-center py-5">Laden...</div>

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Telefon-Provisioning</h4>
      </div>

      <Alert variant="info" className="mb-4">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Template-basiertes Provisioning:</strong> Erstellen Sie Vorlagen mit allen Einstellungen (BLF-Tasten, Sprache, etc.)
        und weisen Sie dann MAC-Adressen den Vorlagen zu.
      </Alert>

      <Card className="mb-4 border-primary">
        <Card.Header className="bg-primary text-white">
          <i className="bi bi-link-45deg me-2"></i>
          <strong>Provisioning Server URL (Yealink)</strong>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-3">
            Geben Sie diese URL im Web-Interface Ihres Yealink-Telefons unter <strong>Einstellungen &gt; Auto-Provisioning &gt; Server-URL</strong> ein:
          </p>
          <div className="d-flex align-items-center gap-2 mb-3">
            <code className="flex-grow-1 p-2 bg-light border rounded" style={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>
              {window.location.protocol}//{window.location.host}/api/provisioning/config/
            </code>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.protocol}//${window.location.host}/api/provisioning/config/`)
                toast.success('URL kopiert!')
              }}
              title="In Zwischenablage kopieren"
            >
              <i className="bi bi-clipboard"></i>
            </Button>
          </div>
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-muted mb-2"><i className="bi bi-gear me-1"></i> Yealink-Einstellungen:</h6>
              <ul className="small mb-0">
                <li><strong>Power On:</strong> Aktiviert</li>
                <li><strong>Repeatedly:</strong> Nach Bedarf (z.B. einmal täglich)</li>
                <li><strong>Provisioning Mode:</strong> HTTP/HTTPS</li>
                <li><strong>AES Key:</strong> Leer lassen (wird automatisch übertragen)</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6 className="text-muted mb-2"><i className="bi bi-shield-check me-1"></i> Sicherheit:</h6>
              <p className="small mb-0">
                Die erste Provisionierung erfolgt unverschlüsselt. Dabei wird ein AES-Schlüssel an das Telefon übertragen.
                Alle folgenden Konfigurationen werden verschlüsselt übertragen.
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'templates')} className="mb-4">

        {/* TEMPLATES TAB */}
        <Tab eventKey="templates" title={<><i className="bi bi-file-earmark-text me-2"></i>Vorlagen ({templates.length})</>}>
          <div className="d-flex justify-content-end mb-3">
            <Button variant="primary" onClick={() => openTemplateModal()}>
              <i className="bi bi-plus-lg me-1"></i> Neue Vorlage
            </Button>
          </div>

          <div className="table-container">
            <Table hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Modell</th>
                  <th>BLF-Tasten</th>
                  <th>Sprache</th>
                  <th>MACs</th>
                  <th className="text-end">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {templates.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted py-4">Keine Vorlagen vorhanden</td></tr>
                ) : templates.map(t => (
                  <tr key={t.id}>
                    <td>
                      <strong>{t.name}</strong>
                      {t.description && <div className="text-muted small">{t.description}</div>}
                    </td>
                    <td>
                      <Badge bg="secondary">{t.model}</Badge>
                      <div className="small text-muted">{t.model_info?.line_keys || 0} Tasten</div>
                    </td>
                    <td>
                      {(t.blf_keys?.length || 0) > 0 ? (
                        <Badge bg="info">{t.blf_keys.length} konfiguriert</Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{t.language}</td>
                    <td>
                      <Badge bg={t.assignment_count > 0 ? 'success' : 'secondary'}>
                        {t.assignment_count}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openTemplateModal(t)} title="Bearbeiten">
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openCloneModal(t)} title="Klonen">
                        <i className="bi bi-copy"></i>
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => deleteTemplate(t)} title="Löschen">
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Tab>

        {/* ASSIGNMENTS TAB */}
        <Tab eventKey="assignments" title={<><i className="bi bi-ethernet me-2"></i>MAC-Zuweisungen ({assignments.length})</>}>
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex gap-2">
              <Button variant="outline-info" onClick={registerAllWithRPS} disabled={assignments.length === 0}>
                <i className="bi bi-cloud-upload me-1"></i> Alle bei RPS registrieren
              </Button>
              <Button
                variant={rpsProblemCount > 0 ? 'warning' : 'outline-secondary'}
                onClick={openRpsFixModal}
                title="Lokalen RPS-Status gegen das Yealink-Konto abgleichen und Abweichungen bereinigen"
              >
                <i className="bi bi-wrench-adjustable me-1"></i> RPS abgleichen
                {rpsProblemCount > 0 && <Badge bg="danger" className="ms-2">{rpsProblemCount}</Badge>}
              </Button>
            </div>
            <Button variant="primary" onClick={() => openAssignmentModal()} disabled={templates.length === 0}>
              <i className="bi bi-plus-lg me-1"></i> MAC zuweisen
            </Button>
          </div>

          {templates.length === 0 && (
            <Alert variant="warning">Erstellen Sie zuerst eine Vorlage im Tab "Vorlagen"</Alert>
          )}

          <div className="table-container">
            <Table hover>
              <thead>
                <tr>
                  <th>MAC-Adresse</th>
                  <th>Vorlage</th>
                  <th>Nebenstelle</th>
                  <th>Beschreibung</th>
                  <th>Letzte Provisionierung</th>
                  <th>Verschlüsselung</th>
                  <th>RPS</th>
                  <th className="text-end">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {assignments.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted py-4">Keine MAC-Zuweisungen vorhanden</td></tr>
                ) : assignments.map(a => (
                  <tr key={a.id}>
                    <td>
                      <code>{a.mac_address}</code>
                      <div className="small text-muted">{a.model}</div>
                    </td>
                    <td>{a.template_name}</td>
                    <td>
                      {a.is_dect ? (
                        a.dect_accounts && a.dect_accounts.length > 0 ? (
                          <div>
                            <Badge bg="info" className="me-1">
                              <i className="bi bi-broadcast me-1"></i>DECT
                            </Badge>
                            <span className="small">{a.dect_accounts.length} Handsets</span>
                          </div>
                        ) : (
                          <Badge bg="warning">
                            <i className="bi bi-broadcast me-1"></i>DECT - nicht konfiguriert
                          </Badge>
                        )
                      ) : a.extension ? (
                        <span>{a.extension.extension} - {a.extension.name}</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{a.description || '-'}</td>
                    <td>
                      {a.last_provisioned ? (
                        <div>
                          <Badge bg="success" className="me-2">
                            <i className="bi bi-check-circle me-1"></i>OK
                          </Badge>
                          <div className="small text-muted mt-1">
                            {new Date(a.last_provisioned).toLocaleString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      ) : (
                        <Badge bg="warning">
                          <i className="bi bi-clock me-1"></i>Wartend
                        </Badge>
                      )}
                    </td>
                    <td>
                      {a.encryption_active ? (
                        <Badge bg="success" title="Konfiguration wird verschlüsselt übertragen">
                          <i className="bi bi-lock-fill me-1"></i>Aktiv
                        </Badge>
                      ) : a.key_provisioned ? (
                        <Badge bg="warning" title="Schlüssel übertragen, aber noch nicht aktiv">
                          <i className="bi bi-unlock me-1"></i>Pending
                        </Badge>
                      ) : (
                        <Badge bg="secondary" title="Erst-Provisionierung steht aus">
                          <i className="bi bi-unlock me-1"></i>Offen
                        </Badge>
                      )}
                    </td>
                    <td>
                      {a.rps_eol ? (
                        <Badge bg="warning" text="dark" title="Dieses Modell wird von Yealink RPS nicht mehr unterstützt (End-of-Life). Bitte verwenden Sie manuelle Provisioning-URLs.">
                          <i className="bi bi-exclamation-triangle me-1"></i>EOL
                        </Badge>
                      ) : a.rps_registered ? (
                        <div className="d-flex align-items-center gap-1">
                          {rpsLive[macKey(a.mac_address)]?.state === 'missing' ? (
                            <Badge bg="danger" style={{cursor: 'help'}} title={`Laut Datenbank registriert (${a.rps_registered_at ? new Date(a.rps_registered_at).toLocaleString('de-DE') : ''}), das Gerät liegt aber nicht in unserem Yealink-Konto — es wurde dort gelöscht oder gehört einem anderen Provider. Das Telefon wird beim Booten NICHT zu uns umgeleitet.`}>
                              <i className="bi bi-cloud-slash me-1"></i>Fehlt bei Yealink
                            </Badge>
                          ) : rpsLive[macKey(a.mac_address)]?.state === 'no_url' ? (
                            <Badge bg="warning" text="dark" style={{cursor: 'help'}} title={`Im Yealink-Konto vorhanden, zeigt aber auf "${rpsLive[macKey(a.mac_address)]?.server_url || '(keine URL)'}" statt auf diesen Server.`}>
                              <i className="bi bi-exclamation-triangle me-1"></i>Abweichend
                            </Badge>
                          ) : (
                            <Badge bg="success" title={`Registriert: ${a.rps_registered_at ? new Date(a.rps_registered_at).toLocaleString('de-DE') : ''}${rpsLive[macKey(a.mac_address)] ? ' — bei Yealink bestätigt' : ''}`}>
                              <i className="bi bi-cloud-check me-1"></i>Aktiv
                            </Badge>
                          )}
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => checkRPSStatus(a)}
                            title="Status bei Yealink prüfen"
                          >
                            <i className="bi bi-eye"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => unregisterFromRPS(a)}
                            title="RPS-Registrierung löschen"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => registerWithRPS(a)}
                            title="Erneut bei RPS registrieren"
                          >
                            <i className="bi bi-arrow-clockwise"></i>
                          </Button>
                        </div>
                      ) : a.rps_last_error ? (
                        <div className="d-flex align-items-center gap-1">
                          <Badge bg="danger" title={a.rps_last_error} style={{cursor: 'help'}}>
                            <i className="bi bi-cloud-slash me-1"></i>Fehler
                          </Badge>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => checkRPSStatus(a)}
                            title="Status bei Yealink prüfen"
                          >
                            <i className="bi bi-eye"></i>
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => resetRPSStatus(a)}
                            title="Status zurücksetzen und erneut versuchen"
                          >
                            <i className="bi bi-arrow-clockwise"></i>
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-1">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => checkRPSStatus(a)}
                            title="Status bei Yealink prüfen"
                          >
                            <i className="bi bi-eye"></i>
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => registerWithRPS(a)}
                            title="Bei Yealink RPS registrieren"
                          >
                            <i className="bi bi-cloud-upload me-1"></i>RPS
                          </Button>
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => openAssignmentModal(a)}
                        title="Zuweisung bearbeiten"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="me-1"
                        onClick={() => showConfigPreview(a)}
                        title="Konfiguration anzeigen"
                      >
                        <i className="bi bi-file-code"></i>
                      </Button>
                      {a.extension && (
                        <>
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="me-1"
                            onClick={() => triggerReprovision(a, false)}
                            title="Config neu laden (ohne Reboot)"
                          >
                            <i className="bi bi-arrow-repeat"></i>
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="me-1"
                            onClick={() => triggerReprovision(a, true)}
                            title="Config neu laden + Reboot"
                          >
                            <i className="bi bi-power"></i>
                          </Button>
                        </>
                      )}
                      {a.encryption_active && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="me-1"
                          onClick={() => resetEncryptionKey(a)}
                          title="Verschlüsselung zurücksetzen"
                        >
                          <i className="bi bi-key"></i>
                        </Button>
                      )}
                      <Button variant="outline-danger" size="sm" onClick={() => deleteAssignment(a)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Tab>

        {/* SOFTPHONES TAB */}
        <Tab eventKey="softphones" title={<><i className="bi bi-phone me-2"></i>Softphones</>}>
          <Alert variant="info" className="mb-4">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Softphone-Provisioning:</strong> Generieren Sie QR-Codes, um Linphone, Zoiper, Groundwire, it64PbxPhone oder andere Softphones schnell zu konfigurieren.
          </Alert>

          <Row>
            <Col md={6}>
              <Card>
                <Card.Header><i className="bi bi-gear me-2"></i>Konfiguration</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Softphone-Typ *</Form.Label>
                    <Form.Select
                      value={selectedSoftphone}
                      onChange={(e) => {
                        setSelectedSoftphone(e.target.value)
                        setQrCodeData(null)
                      }}
                    >
                      {softphoneTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Form.Select>
                    {softphoneTypes.find(t => t.id === selectedSoftphone) && (
                      <Form.Text className="text-muted">
                        {softphoneTypes.find(t => t.id === selectedSoftphone)?.description}
                        {' • '}
                        {softphoneTypes.find(t => t.id === selectedSoftphone)?.platforms.join(', ')}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Nebenstelle *</Form.Label>
                    <Form.Select
                      value={selectedExtension}
                      onChange={(e) => {
                        setSelectedExtension(e.target.value)
                        setQrCodeData(null)
                      }}
                    >
                      <option value="">-- Nebenstelle wählen --</option>
                      {extensions.map(ext => (
                        <option key={ext.id} value={ext.id}>
                          {ext.extension} - {ext.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Server-IP oder Hostname *</Form.Label>
                    <Form.Control
                      type="text"
                      value={serverIp}
                      onChange={(e) => setServerIp(e.target.value)}
                      placeholder="z.B. pbx.firma.de oder 192.168.1.100"
                    />
                    <Form.Text className="text-muted">
                      Die IP-Adresse oder der Hostname, unter dem der SIP-Server erreichbar ist.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Transport</Form.Label>
                    <Form.Select
                      value={transport}
                      onChange={(e) => setTransport(e.target.value)}
                    >
                      <option value="UDP">UDP (Standard)</option>
                      <option value="TCP">TCP</option>
                      <option value="TLS">TLS (verschlüsselt)</option>
                    </Form.Select>
                  </Form.Group>

                  <Button
                    variant="primary"
                    onClick={generateQrCode}
                    disabled={generatingQr || !selectedExtension || !serverIp}
                    className="w-100"
                  >
                    {generatingQr ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Generiere...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-qr-code me-2"></i>
                        QR-Code generieren
                      </>
                    )}
                  </Button>
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Header><i className="bi bi-info-circle me-2"></i>Anleitung</Card.Header>
                <Card.Body>
                  <p className="text-muted mb-2">Öffnen Sie die Softphone-App auf Ihrem Smartphone und scannen Sie den QR-Code:</p>
                  <ul className="mb-0" style={{ fontSize: '0.9rem' }}>
                    <li><strong>Linphone:</strong> Assistenten → Per QR-Code konfigurieren</li>
                    <li><strong>Zoiper:</strong> Einstellungen → Konten → QR-Code scannen</li>
                    <li><strong>Groundwire/Cloud Softphone:</strong> QR-Code mit Kamera-App scannen (csc:// Link öffnet die App automatisch)</li>
                    <li><strong>it64PbxPhone:</strong> Einstellungen → QR-Code scannen</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Header><i className="bi bi-qr-code me-2"></i>QR-Code</Card.Header>
                <Card.Body>
                  {qrCodeData ? (
                    <div className="text-center">
                      <h5 className="mb-2">{qrCodeData.name}</h5>
                      <div className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                        <i className="bi bi-phone me-1"></i> {qrCodeData.softphone_name}
                      </div>
                      <img
                        src={qrCodeData.qr_code}
                        alt="QR Code"
                        style={{ maxWidth: '250px', border: '1px solid #dee2e6', borderRadius: '8px' }}
                      />
                      <div className="mt-3">
                        <Button variant="outline-primary" size="sm" className="me-2" onClick={downloadQrCode}>
                          <i className="bi bi-download me-1"></i> Download
                        </Button>
                        <Button variant="outline-secondary" size="sm" className="me-2" onClick={printQrCode}>
                          <i className="bi bi-printer me-1"></i> Drucken
                        </Button>
                        <Button variant="outline-success" size="sm" onClick={sendQrCodeEmail}>
                          <i className="bi bi-envelope me-1"></i> E-Mail
                        </Button>
                      </div>

                      <Alert variant="secondary" className="mt-3 text-start" style={{ fontSize: '0.85rem' }}>
                        <strong>Konfigurationsdaten:</strong>
                        <div className="mt-2">
                          <div><strong>Server:</strong> {qrCodeData.config.domain}:{qrCodeData.config.port}</div>
                          <div><strong>Benutzer:</strong> {qrCodeData.config.username}</div>
                          <div><strong>Passwort:</strong> {qrCodeData.config.password}</div>
                          <div><strong>Transport:</strong> {qrCodeData.config.transport.toUpperCase()}</div>
                          {qrCodeData.config.includes_ldap && (
                            <div className="mt-2 text-success">
                              <i className="bi bi-check-circle me-1"></i>
                              <strong>LDAP-Verzeichnis:</strong> Enthalten
                            </div>
                          )}
                        </div>
                      </Alert>

                      {qrCodeData.softphone === 'groundwire' && (
                        <Alert variant="info" className="mt-2 text-start" style={{ fontSize: '0.8rem' }}>
                          <i className="bi bi-book me-2"></i>
                          <strong>Telefonbuch in Groundwire einrichten:</strong>
                          <ol className="mb-1 mt-2 ps-3">
                            <li>Öffnen Sie Groundwire → Einstellungen → Erweitert</li>
                            <li>Scrollen Sie zu "Kontaktquellen"</li>
                            <li>Aktivieren Sie "Web Service Contacts"</li>
                            <li>Geben Sie diese URL ein:</li>
                          </ol>
                          <code className="d-block p-2 bg-dark text-light rounded" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                            {phonebookApi.getGroundwireContactsUrl()}
                          </code>
                        </Alert>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      <i className="bi bi-qr-code" style={{ fontSize: '4rem' }}></i>
                      <p className="mt-3">Wählen Sie Softphone und Nebenstelle, dann generieren Sie einen QR-Code.</p>
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Header><i className="bi bi-exclamation-triangle text-warning me-2"></i>Push-Benachrichtigungen</Card.Header>
                <Card.Body style={{ fontSize: '0.85rem' }}>
                  <table className="table table-sm table-borderless mb-2">
                    <thead>
                      <tr>
                        <th style={{ width: '30%' }}>App</th>
                        <th style={{ width: '35%' }}>iOS</th>
                        <th style={{ width: '35%' }}>Android</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Linphone</strong></td>
                        <td><span className="text-danger">Kein Push</span></td>
                        <td><span className="text-success">OK</span></td>
                      </tr>
                      <tr>
                        <td><strong>Oflow</strong></td>
                        <td><span className="text-success">Push inkl.</span></td>
                        <td><span className="text-success">OK</span></td>
                      </tr>
                      <tr>
                        <td><strong>Groundwire</strong></td>
                        <td><span className="text-success">Push inkl.</span></td>
                        <td><span className="text-success">Push inkl.</span></td>
                      </tr>
                      <tr>
                        <td><strong>Zoiper</strong></td>
                        <td><span className="text-warning">Push $</span></td>
                        <td><span className="text-success">OK</span></td>
                      </tr>
                      <tr>
                        <td><strong>it64PbxPhone</strong></td>
                        <td><span className="text-success">Push inkl.</span></td>
                        <td><span className="text-success">Push inkl.</span></td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="text-muted">
                    <i className="bi bi-lightbulb me-1"></i>
                    <strong>Empfehlung:</strong> it64PbxPhone (iPhone, Android, Windows)
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>

      {/* TEMPLATE MODAL */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleTemplateSubmit}>
          <Modal.Body>
            <Tabs defaultActiveKey="basic">
              <Tab eventKey="basic" title="Grundeinstellungen">
                <div className="pt-3">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          value={templateForm.name}
                          onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })}
                          required
                          placeholder="z.B. Standard T46U"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Telefon-Modell</Form.Label>
                        <Form.Select
                          value={templateForm.model}
                          onChange={e => setTemplateForm({ ...templateForm, model: e.target.value })}
                        >
                          {models.map(m => (
                            <option key={m.id} value={m.id}>{m.name} ({m.line_keys} Tasten)</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Beschreibung</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={templateForm.description}
                      onChange={e => setTemplateForm({ ...templateForm, description: e.target.value })}
                      placeholder="Optionale Beschreibung..."
                    />
                  </Form.Group>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Sprache</Form.Label>
                        <Form.Select value={templateForm.language} onChange={e => setTemplateForm({ ...templateForm, language: e.target.value })}>
                          {languages.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Zeitzone</Form.Label>
                        <Form.Select value={templateForm.timezone} onChange={e => setTemplateForm({ ...templateForm, timezone: e.target.value })}>
                          {timezones.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Standard-Klingelton</Form.Label>
                        <Form.Select value={templateForm.ringtone} onChange={e => setTemplateForm({ ...templateForm, ringtone: e.target.value })}>
                          {ringtones.map(r => <option key={r.name} value={r.name}>{r.display_name}{r.type === 'custom' ? ' (Eigener)' : ''}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Klingelton intern</Form.Label>
                        <Form.Select value={templateForm.ringtone_internal} onChange={e => setTemplateForm({ ...templateForm, ringtone_internal: e.target.value })}>
                          {ringtones.map(r => <option key={r.name} value={r.name}>{r.display_name}{r.type === 'custom' ? ' (Eigener)' : ''}</option>)}
                        </Form.Select>
                        <Form.Text className="text-muted">Für Anrufe von internen Nebenstellen</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Klingelton extern</Form.Label>
                        <Form.Select value={templateForm.ringtone_external} onChange={e => setTemplateForm({ ...templateForm, ringtone_external: e.target.value })}>
                          {ringtones.map(r => <option key={r.name} value={r.name}>{r.display_name}{r.type === 'custom' ? ' (Eigener)' : ''}</option>)}
                        </Form.Select>
                        <Form.Text className="text-muted">Für Anrufe über Trunks (extern)</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Klingelton TAPI</Form.Label>
                        <Form.Select value={templateForm.ringtone_tapi} onChange={e => setTemplateForm({ ...templateForm, ringtone_tapi: e.target.value })}>
                          {ringtones.map(r => <option key={r.name} value={r.name}>{r.display_name}{r.type === 'custom' ? ' (Eigener)' : ''}</option>)}
                        </Form.Select>
                        <Form.Text className="text-muted">Für TAPI/Click-to-Dial Anrufe</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  {/* Custom Ringtone Upload Section */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <Card className="bg-light">
                        <Card.Body className="py-2">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <strong>Eigene Klingeltöne</strong>
                              <Form.Text className="d-block text-muted">
                                WAV-Format, max. 300KB, 16-bit PCM, 8/16kHz, Mono
                              </Form.Text>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="file"
                                id="ringtone-upload"
                                accept=".wav"
                                style={{ display: 'none' }}
                                onChange={handleRingtoneUpload}
                              />
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => document.getElementById('ringtone-upload')?.click()}
                                disabled={uploadingRingtone}
                              >
                                {uploadingRingtone ? 'Lädt...' : 'Klingelton hochladen'}
                              </Button>
                            </div>
                          </div>
                          {ringtones.filter(r => r.type === 'custom').length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted">Eigene Klingeltöne:</small>
                              <div className="d-flex flex-wrap gap-2 mt-1">
                                {ringtones.filter(r => r.type === 'custom').map(r => (
                                  <Badge key={r.name} bg="secondary" className="d-flex align-items-center gap-1" style={{ fontSize: '0.85em' }}>
                                    {r.display_name}
                                    <span
                                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                                      onClick={() => handleRingtoneDelete(r.name)}
                                      title="Löschen"
                                    >×</span>
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>NTP-Server</Form.Label>
                        <Form.Control
                          value={templateForm.ntp_server}
                          onChange={e => setTemplateForm({ ...templateForm, ntp_server: e.target.value })}
                          placeholder="pool.ntp.org"
                        />
                        <Form.Text className="text-muted">Zeitserver für automatische Zeitsynchronisation</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Zeitformat</Form.Label>
                        <Form.Select value={templateForm.time_format} onChange={e => setTemplateForm({ ...templateForm, time_format: parseInt(e.target.value) })}>
                          {timeFormats.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Datumsformat</Form.Label>
                        <Form.Select value={templateForm.date_format} onChange={e => setTemplateForm({ ...templateForm, date_format: parseInt(e.target.value) })}>
                          {dateFormats.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          id="daylight-saving"
                          label="Sommerzeit automatisch"
                          checked={templateForm.daylight_saving}
                          onChange={e => setTemplateForm({ ...templateForm, daylight_saving: e.target.checked })}
                        />
                        <Form.Text className="text-muted">Automatische Umstellung auf Sommer-/Winterzeit</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={8}>
                      <Card className="bg-light">
                        <Card.Body className="py-2">
                          <Form.Check
                            type="switch"
                            id="first-key-as-line"
                            label={<><strong>Erste DSS-Taste als Leitung konfigurieren</strong></>}
                            checked={templateForm.first_key_as_line}
                            onChange={e => setTemplateForm({ ...templateForm, first_key_as_line: e.target.checked })}
                          />
                          <Form.Text className="text-muted">
                            Wenn aktiviert, wird die erste Funktionstaste als Leitungstaste für den Account konfiguriert.
                            Deaktivieren Sie diese Option, wenn Sie alle Tasten frei für BLF/Kurzwahl nutzen möchten.
                          </Form.Text>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>DSS-Tasten Beschriftungslänge</Form.Label>
                        <Form.Select
                          value={templateForm.dss_key_width}
                          onChange={e => setTemplateForm({ ...templateForm, dss_key_width: parseInt(e.target.value) })}
                        >
                          <option value={0}>Standard</option>
                          <option value={1}>Erweitert</option>
                          <option value={2}>Mittel</option>
                        </Form.Select>
                        <Form.Text className="text-muted">Breite der DSS-Tasten im Display</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Card className="mb-3">
                        <Card.Header>
                          <Form.Check
                            type="switch"
                            id="cti-auto-answer"
                            label={<strong>Fernsteuerung durch die Softphone-App erlauben (CTI)</strong>}
                            checked={templateForm.cti_auto_answer}
                            onChange={e => setTemplateForm({ ...templateForm, cti_auto_answer: e.target.checked })}
                          />
                        </Card.Header>
                        <Card.Body className="py-2">
                          <p className="text-muted small mb-2">
                            Erlaubt Wählen und Annehmen vom PC aus (Softphone → Einstellungen →
                            Telefonie-Partner → „Tischtelefon"). Das Telefon hebt dabei nur bei
                            Anrufen ab, welche die Telefonanlage eigens dafür kennzeichnet
                            (Auto-Answer-Header). <strong>Normale Anrufe klingeln wie gewohnt.</strong>
                          </p>
                          <Alert variant="warning" className="py-2 small mb-3">
                            Nicht zu verwechseln mit <strong>„Auto-Answer"</strong> (unten): jenes
                            nimmt <em>jeden</em> eingehenden Anruf automatisch an.
                          </Alert>
                          {templateForm.cti_auto_answer && (
                            <Form.Check
                              type="switch"
                              id="cti-auto-answer-tone"
                              className="mb-3"
                              label="Warnton, bevor das Telefon selbst abhebt"
                              checked={templateForm.cti_auto_answer_tone}
                              onChange={e => setTemplateForm({ ...templateForm, cti_auto_answer_tone: e.target.checked })}
                            />
                          )}
                          <Row>
                            <Col md={6}>
                              <Form.Check
                                type="switch"
                                id="sip-trust-server-only"
                                label="Nur SIP-Nachrichten des eigenen Servers akzeptieren"
                                checked={templateForm.sip_trust_server_only}
                                onChange={e => setTemplateForm({ ...templateForm, sip_trust_server_only: e.target.checked })}
                              />
                              <Form.Text className="text-muted">
                                Empfohlen: blockt fremde SIP-Pakete („Ghost Calls") und lässt nur
                                Steuerbefehle der eigenen Telefonanlage zu.
                              </Form.Text>
                            </Col>
                            <Col md={6}>
                              <Form.Check
                                type="switch"
                                id="allow-ip-call"
                                label="Direkte IP-Anrufe erlauben"
                                checked={templateForm.allow_ip_call}
                                onChange={e => setTemplateForm({ ...templateForm, allow_ip_call: e.target.checked })}
                              />
                              <Form.Text className="text-muted">
                                Für die Fernsteuerung nicht nötig — aus Sicherheitsgründen aus lassen.
                              </Form.Text>
                            </Col>
                          </Row>
                          <Row className="mt-2">
                            <Col md={12}>
                              <Form.Check
                                type="switch"
                                id="cti-register-mac"
                                label="MAC-Adresse bei der SIP-Registrierung mitsenden"
                                checked={templateForm.cti_register_mac}
                                onChange={e => setTemplateForm({ ...templateForm, cti_register_mac: e.target.checked })}
                              />
                              <Form.Text className="text-muted">
                                Geräteechtes Unterscheidungsmerkmal für die Geräteauswahl der App,
                                wenn <strong>mehrere Tischtelefone</strong> an einer Nebenstelle hängen
                                (übersteht IP-Wechsel und Neustarts). Greift erst nach
                                Neu-Provisionierung/Neustart des Telefons.
                              </Form.Text>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        id="call-waiting"
                        className="mb-2"
                        label="Anklopfen (Call Waiting)"
                        checked={templateForm.call_waiting}
                        onChange={e => setTemplateForm({ ...templateForm, call_waiting: e.target.checked })}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        id="auto-answer-all"
                        className="mb-2"
                        label="Auto-Answer: JEDEN Anruf automatisch annehmen"
                        checked={templateForm.auto_answer}
                        onChange={e => setTemplateForm({ ...templateForm, auto_answer: e.target.checked })}
                      />
                      <Form.Text className="text-muted">
                        Nur für Sonderfälle (z.B. Türsprechstelle). Für die CTI-Fernsteuerung
                        stattdessen die Option oben verwenden.
                      </Form.Text>
                    </Col>
                  </Row>
                </div>
              </Tab>

              <Tab eventKey="blf" title={`BLF-Tasten (${templateForm.blf_keys.length})`}>
                <div className="pt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-3">
                      <span className="text-muted">
                        {getModelInfo(templateForm.model)?.line_keys || 0} Tasten verfügbar
                      </span>
                      <ButtonGroup size="sm">
                        <Button
                          variant={blfViewMode === 'compact' ? 'primary' : 'outline-primary'}
                          onClick={() => setBlfViewMode('compact')}
                          title="Kompakte Ansicht"
                        >
                          <i className="bi bi-table"></i>
                        </Button>
                        <Button
                          variant={blfViewMode === 'accordion' ? 'primary' : 'outline-primary'}
                          onClick={() => setBlfViewMode('accordion')}
                          title="Gruppen-Ansicht (10er-Blöcke)"
                        >
                          <i className="bi bi-list-nested"></i>
                        </Button>
                        <Button
                          variant={blfViewMode === 'detail' ? 'primary' : 'outline-primary'}
                          onClick={() => setBlfViewMode('detail')}
                          title="Detail-Ansicht"
                        >
                          <i className="bi bi-card-list"></i>
                        </Button>
                      </ButtonGroup>
                    </div>
                    <Button variant="outline-primary" size="sm" onClick={addBlfKey}>
                      <i className="bi bi-plus-lg me-1"></i> Taste hinzufügen
                    </Button>
                  </div>

                  {templateForm.blf_keys.length === 0 ? (
                    <div className="text-center text-muted py-4">Keine BLF-Tasten konfiguriert</div>
                  ) : blfViewMode === 'compact' ? (
                    /* COMPACT TABLE VIEW */
                    <div className="table-responsive">
                      <Table size="sm" hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{width: '50px'}}>#</th>
                            <th style={{width: '180px'}}>Typ</th>
                            <th style={{width: '120px'}}>Label</th>
                            <th>Wert</th>
                            <th style={{width: '40px'}}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {getSortedBlfKeys().map((key) => (
                            <tr key={key.position}>
                              <td>
                                <Badge bg="secondary" className="font-monospace">{key.position}</Badge>
                              </td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={key.type}
                                  onChange={e => {
                                    const newType = e.target.value
                                    const updates: Partial<BLFKey> = { type: newType, value: '' }
                                    if (newType === 'queue_status' && !key.label) updates.label = 'Queue'
                                    updateBlfKeyMultiple(key.position, updates)
                                  }}
                                  style={{fontSize: '0.8rem'}}
                                >
                                  {blfTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </Form.Select>
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  placeholder={key.type === 'none' ? '(Platzhalter)' : 'Label'}
                                  value={key.type === 'none' ? '' : key.label}
                                  onChange={e => updateBlfKey(key.position, 'label', e.target.value)}
                                  style={{fontSize: '0.8rem'}}
                                  disabled={key.type === 'none'}
                                />
                              </td>
                              <td>
                                {key.type === 'none' ? (
                                  <Form.Control size="sm" value="" disabled placeholder="(leer)" style={{fontSize: '0.8rem'}} />
                                ) : key.type === 'queue_status' || key.type === 'queue_login' ? (
                                  <Form.Select size="sm" value={key.value} onChange={e => {
                                    const selectedQueue = queues.find(q => q.name === e.target.value)
                                    updateBlfKeyMultiple(key.position, {
                                      value: e.target.value,
                                      label: selectedQueue ? (selectedQueue.display_name || selectedQueue.name) : key.label
                                    })
                                  }} style={{fontSize: '0.8rem'}}>
                                    <option value="">-- Warteschlange --</option>
                                    {queues.map(q => <option key={q.id} value={q.name}>{q.display_name || q.name}</option>)}
                                  </Form.Select>
                                ) : key.type === 'blf' || key.type === 'voicemail_other' || key.type === 'voicemail_greeting' || key.type === 'voicemail_greeting_menu' || key.type === 'intercom' || key.type === 'transfer' ? (
                                  <Form.Select size="sm" value={key.value} onChange={e => {
                                    const selectedExt = extensions.find(ext => ext.extension === e.target.value)
                                    updateBlfKeyMultiple(key.position, {
                                      value: e.target.value,
                                      label: (selectedExt && !key.label) ? selectedExt.name : key.label
                                    })
                                  }} style={{fontSize: '0.8rem'}}>
                                    <option value="">-- Nebenstelle --</option>
                                    {extensions.map(ext => <option key={ext.id} value={ext.extension}>{ext.extension} - {ext.name}</option>)}
                                  </Form.Select>
                                ) : key.type === 'night_mode' ? (
                                  <Form.Select size="sm" value={key.value} onChange={e => {
                                    const selectedNm = nightModes.find(nm => nm.id === e.target.value)
                                    updateBlfKeyMultiple(key.position, { value: e.target.value, label: selectedNm ? selectedNm.name : key.label })
                                  }} style={{fontSize: '0.8rem'}}>
                                    <option value="">-- Nachtschaltung --</option>
                                    {nightModes.map(nm => <option key={nm.id} value={nm.id}>{nm.name}{nm.feature_code ? ` (${nm.feature_code})` : ''}</option>)}
                                  </Form.Select>
                                ) : key.type === 'call_park' ? (
                                  <Form.Select size="sm" value={key.value} onChange={e => {
                                    const selectedPl = parkingLots.find(pl => pl.parking_extension === e.target.value)
                                    updateBlfKeyMultiple(key.position, {
                                      value: e.target.value,
                                      label: selectedPl ? selectedPl.name : key.label
                                    })
                                  }} style={{fontSize: '0.8rem'}}>
                                    <option value="">-- Parkplatz --</option>
                                    {parkingLots.map(pl => <option key={pl.id} value={pl.parking_extension}>{pl.parking_extension} - {pl.name} ({pl.start_slot}-{pl.end_slot})</option>)}
                                  </Form.Select>
                                ) : key.type === 'system_recording' ? (
                                  <Form.Select size="sm" value={key.value} onChange={e => {
                                    const selectedRec = recordings.find(r => r.dial_code === e.target.value)
                                    updateBlfKeyMultiple(key.position, {
                                      value: e.target.value,
                                      label: selectedRec ? selectedRec.name : key.label
                                    })
                                  }} style={{fontSize: '0.8rem'}}>
                                    <option value="">-- Aufnahme wählen --</option>
                                    {recordings.filter(r => r.dial_code).map(r => <option key={r.id} value={r.dial_code!}>*89{r.dial_code} - {r.name}</option>)}
                                  </Form.Select>
                                ) : key.type === 'voicemail' ? (
                                  <span className="text-muted small">Eigene Mailbox (*97)</span>
                                ) : (
                                  <Form.Control size="sm" placeholder="Wert/Nummer" value={key.value} onChange={e => updateBlfKey(key.position, 'value', e.target.value)} style={{fontSize: '0.8rem'}} />
                                )}
                              </td>
                              <td className="text-end">
                                <Button variant="link" className="text-danger p-0" size="sm" onClick={() => removeBlfKey(key.position)}>
                                  <i className="bi bi-x-lg"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : blfViewMode === 'accordion' ? (
                    /* ACCORDION GROUP VIEW - 10er Blöcke */
                    (() => {
                      const sortedKeys = getSortedBlfKeys()
                      const maxPos = sortedKeys.length > 0 ? Math.max(...sortedKeys.map(k => k.position)) : 0
                      const groups: { start: number; end: number; keys: BLFKey[] }[] = []

                      // Create groups of 10
                      for (let i = 1; i <= Math.max(maxPos, 10); i += 10) {
                        const groupKeys = sortedKeys.filter(k => k.position >= i && k.position < i + 10)
                        if (groupKeys.length > 0 || i === 1) {
                          groups.push({ start: i, end: i + 9, keys: groupKeys })
                        }
                      }

                      const getKeyTypeBadge = (type: string) => {
                        const typeInfo = blfTypes.find(t => t.value === type)
                        return typeInfo ? typeInfo.label : type
                      }

                      return (
                        <Accordion defaultActiveKey="0">
                          {groups.map((group, idx) => (
                            <Accordion.Item key={idx} eventKey={String(idx)}>
                              <Accordion.Header>
                                <div className="d-flex align-items-center gap-3 w-100">
                                  <Badge bg="secondary" className="font-monospace">
                                    {group.start}-{group.end}
                                  </Badge>
                                  <span className="fw-bold">
                                    Tasten {group.start} bis {group.end}
                                  </span>
                                  <Badge bg={group.keys.length > 0 ? 'primary' : 'light'} text={group.keys.length > 0 ? undefined : 'muted'}>
                                    {group.keys.length} belegt
                                  </Badge>
                                  {group.keys.length > 0 && (
                                    <span className="text-muted small ms-auto me-3">
                                      {group.keys.slice(0, 3).map(k => k.label || getKeyTypeBadge(k.type)).join(', ')}
                                      {group.keys.length > 3 && ` +${group.keys.length - 3}`}
                                    </span>
                                  )}
                                </div>
                              </Accordion.Header>
                              <Accordion.Body className="p-0">
                                {group.keys.length === 0 ? (
                                  <div className="text-center text-muted py-3">Keine Tasten in diesem Bereich</div>
                                ) : (
                                  <Table size="sm" hover className="mb-0">
                                    <thead className="table-light">
                                      <tr>
                                        <th style={{width: '50px'}}>#</th>
                                        <th style={{width: '180px'}}>Typ</th>
                                        <th style={{width: '120px'}}>Label</th>
                                        <th>Wert</th>
                                        <th style={{width: '40px'}}></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {group.keys.map((key) => (
                                        <tr key={key.position}>
                                          <td>
                                            <Badge bg="secondary" className="font-monospace">{key.position}</Badge>
                                          </td>
                                          <td>
                                            <Form.Select
                                              size="sm"
                                              value={key.type}
                                              onChange={e => {
                                                const newType = e.target.value
                                                const updates: Partial<BLFKey> = { type: newType, value: '' }
                                                if (newType === 'queue_status' && !key.label) updates.label = 'Queue'
                                                updateBlfKeyMultiple(key.position, updates)
                                              }}
                                              style={{fontSize: '0.8rem'}}
                                            >
                                              {blfTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </Form.Select>
                                          </td>
                                          <td>
                                            <Form.Control
                                              size="sm"
                                              placeholder={key.type === 'none' ? '(Platzhalter)' : 'Label'}
                                              value={key.type === 'none' ? '' : key.label}
                                              onChange={e => updateBlfKey(key.position, 'label', e.target.value)}
                                              style={{fontSize: '0.8rem'}}
                                              disabled={key.type === 'none'}
                                            />
                                          </td>
                                          <td>
                                            {key.type === 'none' ? (
                                              <Form.Control size="sm" value="" disabled placeholder="(leer)" style={{fontSize: '0.8rem'}} />
                                            ) : key.type === 'queue_status' || key.type === 'queue_login' ? (
                                              <Form.Select size="sm" value={key.value} onChange={e => {
                                                const selectedQueue = queues.find(q => q.name === e.target.value)
                                                updateBlfKeyMultiple(key.position, {
                                                  value: e.target.value,
                                                  label: selectedQueue ? (selectedQueue.display_name || selectedQueue.name) : key.label
                                                })
                                              }} style={{fontSize: '0.8rem'}}>
                                                <option value="">-- Warteschlange --</option>
                                                {queues.map(q => <option key={q.id} value={q.name}>{q.display_name || q.name}</option>)}
                                              </Form.Select>
                                            ) : key.type === 'blf' || key.type === 'voicemail_other' || key.type === 'voicemail_greeting' || key.type === 'voicemail_greeting_menu' || key.type === 'intercom' || key.type === 'transfer' ? (
                                              <Form.Select size="sm" value={key.value} onChange={e => {
                                                const selectedExt = extensions.find(ext => ext.extension === e.target.value)
                                                updateBlfKeyMultiple(key.position, {
                                                  value: e.target.value,
                                                  label: (selectedExt && !key.label) ? selectedExt.name : key.label
                                                })
                                              }} style={{fontSize: '0.8rem'}}>
                                                <option value="">-- Nebenstelle --</option>
                                                {extensions.map(ext => <option key={ext.id} value={ext.extension}>{ext.extension} - {ext.name}</option>)}
                                              </Form.Select>
                                            ) : key.type === 'night_mode' ? (
                                              <Form.Select size="sm" value={key.value} onChange={e => {
                                                const selectedNm = nightModes.find(nm => nm.id === e.target.value)
                                                updateBlfKeyMultiple(key.position, { value: e.target.value, label: selectedNm ? selectedNm.name : key.label })
                                              }} style={{fontSize: '0.8rem'}}>
                                                <option value="">-- Nachtschaltung --</option>
                                                {nightModes.map(nm => <option key={nm.id} value={nm.id}>{nm.name}{nm.feature_code ? ` (${nm.feature_code})` : ''}</option>)}
                                              </Form.Select>
                                            ) : key.type === 'call_park' ? (
                                              <Form.Select size="sm" value={key.value} onChange={e => {
                                                const selectedPl = parkingLots.find(pl => pl.parking_extension === e.target.value)
                                                updateBlfKeyMultiple(key.position, {
                                                  value: e.target.value,
                                                  label: selectedPl ? selectedPl.name : key.label
                                                })
                                              }} style={{fontSize: '0.8rem'}}>
                                                <option value="">-- Parkplatz --</option>
                                                {parkingLots.map(pl => <option key={pl.id} value={pl.parking_extension}>{pl.parking_extension} - {pl.name} ({pl.start_slot}-{pl.end_slot})</option>)}
                                              </Form.Select>
                                            ) : key.type === 'system_recording' ? (
                                              <Form.Select size="sm" value={key.value} onChange={e => {
                                                const selectedRec = recordings.find(r => r.dial_code === e.target.value)
                                                updateBlfKeyMultiple(key.position, {
                                                  value: e.target.value,
                                                  label: selectedRec ? selectedRec.name : key.label
                                                })
                                              }} style={{fontSize: '0.8rem'}}>
                                                <option value="">-- Aufnahme wählen --</option>
                                                {recordings.filter(r => r.dial_code).map(r => <option key={r.id} value={r.dial_code!}>*89{r.dial_code} - {r.name}</option>)}
                                              </Form.Select>
                                            ) : key.type === 'voicemail' ? (
                                              <span className="text-muted small">Eigene Mailbox (*97)</span>
                                            ) : (
                                              <Form.Control size="sm" placeholder="Wert/Nummer" value={key.value} onChange={e => updateBlfKey(key.position, 'value', e.target.value)} style={{fontSize: '0.8rem'}} />
                                            )}
                                          </td>
                                          <td className="text-end">
                                            <Button variant="link" className="text-danger p-0" size="sm" onClick={() => removeBlfKey(key.position)}>
                                              <i className="bi bi-x-lg"></i>
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                )}
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </Accordion>
                      )
                    })()
                  ) : (
                    /* DETAIL CARD VIEW */
                    getSortedBlfKeys().map((key, sortIndex) => (
                      <Card
                        key={key.position}
                        className={`mb-2 ${draggedBlfIndex === key.position ? 'border-primary' : ''}`}
                        draggable
                        onDragStart={(e) => handleBlfDragStart(e, key.position)}
                        onDragEnd={handleBlfDragEnd}
                        onDragOver={handleBlfDragOver}
                        onDrop={(e) => handleBlfDrop(e, key.position)}
                        style={{ cursor: 'grab' }}
                      >
                        <Card.Body className="py-2">
                          <Row className="align-items-center">
                            <Col xs="auto" className="pe-0">
                              <div className="d-flex flex-column me-2">
                                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => moveBlfKey(key.position, 'up')} disabled={sortIndex === 0} title="Nach oben">
                                  <i className="bi bi-chevron-up"></i>
                                </Button>
                                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => moveBlfKey(key.position, 'down')} disabled={sortIndex === getSortedBlfKeys().length - 1} title="Nach unten">
                                  <i className="bi bi-chevron-down"></i>
                                </Button>
                              </div>
                            </Col>
                            <Col xs="auto" className="pe-2">
                              <i className="bi bi-grip-vertical text-muted" style={{ cursor: 'grab' }}></i>
                            </Col>
                            <Col xs={1}>
                              <Badge bg="secondary">#{key.position}</Badge>
                            </Col>
                            <Col xs={2}>
                              <Form.Select size="sm" value={key.type} onChange={e => {
                                const newType = e.target.value
                                const updates: Partial<BLFKey> = { type: newType, value: '' }
                                if (newType === 'queue_status' && !key.label) updates.label = 'Queue'
                                updateBlfKeyMultiple(key.position, updates)
                              }}>
                                {blfTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </Form.Select>
                            </Col>
                            <Col xs={2}>
                              <Form.Control size="sm" placeholder={key.type === 'none' ? '(Platzhalter)' : 'Label'} value={key.type === 'none' ? '' : key.label} onChange={e => updateBlfKey(key.position, 'label', e.target.value)} disabled={key.type === 'none'} />
                            </Col>
                            <Col xs={3}>
                              {key.type === 'none' ? (
                                <Form.Control size="sm" value="" disabled placeholder="(leer)" />
                              ) : key.type === 'queue_status' || key.type === 'queue_login' ? (
                                <Form.Select size="sm" value={key.value} onChange={e => {
                                  const selectedQueue = queues.find(q => q.name === e.target.value)
                                  updateBlfKeyMultiple(key.position, {
                                    value: e.target.value,
                                    label: selectedQueue ? (selectedQueue.display_name || selectedQueue.name) : key.label
                                  })
                                }}>
                                  <option value="">-- Warteschlange wählen --</option>
                                  {queues.map(q => <option key={q.id} value={q.name}>{q.display_name || q.name}</option>)}
                                </Form.Select>
                              ) : key.type === 'blf' || key.type === 'voicemail_other' || key.type === 'voicemail_greeting' || key.type === 'voicemail_greeting_menu' || key.type === 'intercom' || key.type === 'transfer' ? (
                                <Form.Select size="sm" value={key.value} onChange={e => {
                                  const selectedExt = extensions.find(ext => ext.extension === e.target.value)
                                  updateBlfKeyMultiple(key.position, {
                                    value: e.target.value,
                                    label: (selectedExt && !key.label) ? selectedExt.name : key.label
                                  })
                                }}>
                                  <option value="">-- Nebenstelle wählen --</option>
                                  {extensions.map(ext => <option key={ext.id} value={ext.extension}>{ext.extension} - {ext.name}</option>)}
                                </Form.Select>
                              ) : key.type === 'night_mode' ? (
                                <Form.Select size="sm" value={key.value} onChange={e => {
                                  const selectedNm = nightModes.find(nm => nm.id === e.target.value)
                                  updateBlfKeyMultiple(key.position, { value: e.target.value, label: selectedNm ? selectedNm.name : key.label })
                                }}>
                                  <option value="">-- Nachtschaltung wählen --</option>
                                  {nightModes.map(nm => <option key={nm.id} value={nm.id}>{nm.name}{nm.feature_code ? ` (${nm.feature_code})` : ''}</option>)}
                                </Form.Select>
                              ) : key.type === 'call_park' ? (
                                <Form.Select size="sm" value={key.value} onChange={e => {
                                  const selectedPl = parkingLots.find(pl => pl.parking_extension === e.target.value)
                                  updateBlfKeyMultiple(key.position, {
                                    value: e.target.value,
                                    label: selectedPl ? selectedPl.name : key.label
                                  })
                                }}>
                                  <option value="">-- Parkplatz wählen --</option>
                                  {parkingLots.map(pl => <option key={pl.id} value={pl.parking_extension}>{pl.parking_extension} - {pl.name} ({pl.start_slot}-{pl.end_slot})</option>)}
                                </Form.Select>
                              ) : key.type === 'system_recording' ? (
                                <Form.Select size="sm" value={key.value} onChange={e => {
                                  const selectedRec = recordings.find(r => r.dial_code === e.target.value)
                                  updateBlfKeyMultiple(key.position, {
                                    value: e.target.value,
                                    label: selectedRec ? selectedRec.name : key.label
                                  })
                                }}>
                                  <option value="">-- Aufnahme wählen --</option>
                                  {recordings.filter(r => r.dial_code).map(r => <option key={r.id} value={r.dial_code!}>*89{r.dial_code} - {r.name}</option>)}
                                </Form.Select>
                              ) : key.type === 'voicemail' ? (
                                <Form.Control size="sm" value="Eigene Mailbox" disabled />
                              ) : (
                                <Form.Control size="sm" placeholder="Wert/Nummer" value={key.value} onChange={e => updateBlfKey(key.position, 'value', e.target.value)} />
                              )}
                            </Col>
                            <Col xs={1}>
                              <Form.Control size="sm" type="number" min={1} max={99} value={key.position} onChange={e => updateBlfKey(key.position, 'position', parseInt(e.target.value))} />
                            </Col>
                            <Col xs={1}>
                              <Button variant="link" className="text-danger p-0" onClick={() => removeBlfKey(key.position)}>
                                <i className="bi bi-x-lg"></i>
                              </Button>
                            </Col>
                          </Row>
                          {key.type === 'queue_status' && (
                            <div className="small text-success mt-1 ms-5"><i className="bi bi-info-circle me-1"></i>Taste wird ROT wenn Anrufer in der Warteschlange warten</div>
                          )}
                          {key.type === 'queue_login' && (
                            <div className="small text-info mt-1 ms-5"><i className="bi bi-info-circle me-1"></i>Toggle: GRÜN = angemeldet, AUS = abgemeldet</div>
                          )}
                          {key.type === 'voicemail' && (
                            <div className="small text-info mt-1 ms-5"><i className="bi bi-info-circle me-1"></i>Ruft eigene Mailbox ab (*97)</div>
                          )}
                          {key.type === 'voicemail_other' && (
                            <div className="small text-info mt-1 ms-5"><i className="bi bi-info-circle me-1"></i>Ruft die Mailbox der gewählten Nebenstelle ab (*98+Nummer)</div>
                          )}
                          {key.type === 'voicemail_greeting' && (
                            <div className="small text-warning mt-1 ms-5"><i className="bi bi-info-circle me-1"></i>Toggle: GRÜN = Abwesend, ROT = Besetzt, AUS = Standard</div>
                          )}
                          {key.type === 'night_mode' && (
                            <div className="small text-warning mt-1 ms-5"><i className="bi bi-info-circle me-1"></i>Toggle: ROT = Nachtmodus aktiv, AUS = Normalbetrieb</div>
                          )}
                        </Card.Body>
                      </Card>
                    ))
                  )}
                </div>
              </Tab>

              <Tab eventKey="display" title="Display">
                <div className="pt-3">
                  <Row>
                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Header><i className="bi bi-image me-2"></i>Hintergrundbild</Card.Header>
                        <Card.Body>
                          {templateForm.wallpaper_file ? (
                            <div className="text-center mb-3">
                              <img
                                src={`/api/provisioning/wallpaper/${templateForm.wallpaper_file}`}
                                alt="Wallpaper"
                                style={{ maxWidth: '200px', maxHeight: '150px', border: '1px solid #dee2e6', borderRadius: '4px' }}
                              />
                              <div className="mt-2">
                                <Badge bg="success">Hochgeladen</Badge>
                                {editingTemplate && (
                                  <Button
                                    variant="link"
                                    className="text-danger p-0 ms-2"
                                    onClick={async () => {
                                      try {
                                        await provisioningApi.deleteWallpaper(editingTemplate.id)
                                        setTemplateForm({ ...templateForm, wallpaper_file: null })
                                        toast.success('Hintergrundbild entfernt')
                                      } catch {
                                        toast.error('Fehler beim Entfernen')
                                      }
                                    }}
                                  >
                                    <i className="bi bi-trash"></i> Entfernen
                                  </Button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center text-muted mb-3 py-3">
                              <i className="bi bi-image" style={{ fontSize: '2rem' }}></i>
                              <p className="mb-0 mt-2">Kein Hintergrundbild</p>
                            </div>
                          )}

                          {editingTemplate && (
                            <Form.Group className="mb-3">
                              <Form.Label>Bild hochladen</Form.Label>
                              <Form.Control
                                type="file"
                                accept="image/jpeg,image/png,image/bmp"
                                onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                  const file = e.target.files?.[0]
                                  if (!file) return
                                  try {
                                    const formData = new FormData()
                                    formData.append('file', file)
                                    const res = await provisioningApi.uploadWallpaper(editingTemplate.id, formData)
                                    setTemplateForm({ ...templateForm, wallpaper_file: res.data.filename })
                                    toast.success('Hintergrundbild hochgeladen')
                                  } catch {
                                    toast.error('Fehler beim Hochladen')
                                  }
                                }}
                              />
                              <Form.Text className="text-muted">
                                Unterstützt: JPG, PNG, BMP. Empfohlene Auflösung: 480x272 (T46U) oder 800x480 (T48U)
                              </Form.Text>
                            </Form.Group>
                          )}

                          {!editingTemplate && (
                            <Alert variant="info" className="mb-0">
                              <i className="bi bi-info-circle me-2"></i>
                              Speichern Sie zuerst die Vorlage, um ein Hintergrundbild hochzuladen.
                            </Alert>
                          )}

                          <Form.Group className="mt-3">
                            <Form.Label>Oder externe URL</Form.Label>
                            <Form.Control
                              type="url"
                              placeholder="https://example.com/wallpaper.jpg"
                              value={templateForm.wallpaper_url}
                              onChange={e => setTemplateForm({ ...templateForm, wallpaper_url: e.target.value })}
                            />
                            <Form.Text className="text-muted">
                              Alternativ können Sie eine externe URL zu einem Bild angeben
                            </Form.Text>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Header><i className="bi bi-display me-2"></i>Display-Einstellungen</Card.Header>
                        <Card.Body>
                          <Form.Group className="mb-3">
                            <Form.Label>Kontrast</Form.Label>
                            <Form.Range
                              min={1}
                              max={10}
                              value={templateForm.contrast}
                              onChange={e => setTemplateForm({ ...templateForm, contrast: parseInt(e.target.value) })}
                            />
                            <div className="d-flex justify-content-between small text-muted">
                              <span>Niedrig (1)</span>
                              <span className="fw-bold">{templateForm.contrast}</span>
                              <span>Hoch (10)</span>
                            </div>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Hintergrundbeleuchtung (Sekunden)</Form.Label>
                            <Form.Control
                              type="number"
                              min={0}
                              max={1800}
                              value={templateForm.backlight_time}
                              onChange={e => setTemplateForm({ ...templateForm, backlight_time: parseInt(e.target.value) || 0 })}
                            />
                            <Form.Text className="text-muted">
                              0 = immer an, max 1800 Sekunden (30 Min)
                            </Form.Text>
                          </Form.Group>
                        </Card.Body>
                      </Card>

                      <Card>
                        <Card.Header><i className="bi bi-moon me-2"></i>Bildschirmschoner</Card.Header>
                        <Card.Body>
                          <Form.Group className="mb-3">
                            <Form.Label>Typ</Form.Label>
                            <Form.Select
                              value={templateForm.screen_saver_type}
                              onChange={e => setTemplateForm({ ...templateForm, screen_saver_type: parseInt(e.target.value) })}
                            >
                              <option value={0}>System (Standard)</option>
                              <option value={1}>Benutzerdefiniert</option>
                              <option value={2}>Server-URL</option>
                            </Form.Select>
                          </Form.Group>

                          <Form.Group>
                            <Form.Label>Wartezeit (Sekunden)</Form.Label>
                            <Form.Control
                              type="number"
                              min={0}
                              max={3600}
                              value={templateForm.screen_saver_wait}
                              onChange={e => setTemplateForm({ ...templateForm, screen_saver_wait: parseInt(e.target.value) || 0 })}
                            />
                            <Form.Text className="text-muted">
                              0 = deaktiviert, Empfohlen: 300 (5 Min)
                            </Form.Text>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md={12}>
                      <Card>
                        <Card.Header><i className="bi bi-lightbulb me-2"></i>Power-LED (rote LED oben)</Card.Header>
                        <Card.Body>
                          <p className="text-muted mb-3">
                            Die rote LED auf der Oberseite des Telefons kann für verschiedene Benachrichtigungen verwendet werden.
                          </p>
                          <Row>
                            <Col md={6}>
                              <Form.Check
                                type="switch"
                                id="power_led_ringing"
                                label="Blinken bei eingehendem Anruf"
                                checked={templateForm.power_led_ringing}
                                onChange={e => setTemplateForm({ ...templateForm, power_led_ringing: e.target.checked })}
                                className="mb-2"
                              />
                              <Form.Check
                                type="switch"
                                id="power_led_mwi"
                                label="Blinken bei neuer Voicemail (MWI)"
                                checked={templateForm.power_led_mwi}
                                onChange={e => setTemplateForm({ ...templateForm, power_led_mwi: e.target.checked })}
                                className="mb-2"
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Check
                                type="switch"
                                id="power_led_talking"
                                label="Leuchten während Gespräch"
                                checked={templateForm.power_led_talking}
                                onChange={e => setTemplateForm({ ...templateForm, power_led_talking: e.target.checked })}
                                className="mb-2"
                              />
                              <Form.Check
                                type="switch"
                                id="power_led_missed_call"
                                label="Blinken bei verpasstem Anruf"
                                checked={templateForm.power_led_missed_call}
                                onChange={e => setTemplateForm({ ...templateForm, power_led_missed_call: e.target.checked })}
                                className="mb-2"
                              />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </Tab>

              <Tab eventKey="voicemail" title="Voicemail/MWI">
                <div className="pt-3">
                  <Card className="mb-3">
                    <Card.Header>
                      <Form.Check
                        type="switch"
                        label={<strong>MWI (Message Waiting Indicator) aktivieren</strong>}
                        checked={templateForm.mwi_enabled}
                        onChange={e => setTemplateForm({ ...templateForm, mwi_enabled: e.target.checked })}
                      />
                    </Card.Header>
                    {templateForm.mwi_enabled && (
                      <Card.Body>
                        <p className="text-muted mb-3">
                          MWI zeigt die Anzahl neuer Voicemail-Nachrichten auf dem Telefon an.
                          Die LED blinkt und ein Symbol erscheint, wenn neue Nachrichten vorhanden sind.
                        </p>

                        <Form.Group className="mb-3">
                          <Form.Label>Überwachte Mailbox</Form.Label>
                          <Form.Select
                            value={templateForm.mwi_mailbox}
                            onChange={e => setTemplateForm({ ...templateForm, mwi_mailbox: e.target.value })}
                          >
                            <option value="">Eigene Mailbox der Nebenstelle</option>
                            <optgroup label="Nebenstellen mit Voicemail">
                              {extensions.filter(ext => ext.voicemail_enabled).map(ext => (
                                <option key={ext.id} value={ext.extension}>
                                  {ext.extension} - {ext.name}
                                </option>
                              ))}
                            </optgroup>
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Wählen Sie eine gemeinsame Mailbox (z.B. Telefonbeantworter) oder lassen Sie es bei "Eigene Mailbox" für die persönliche Voicemail.
                          </Form.Text>
                        </Form.Group>

                        <div className="alert alert-info mb-0">
                          <i className="bi bi-info-circle me-2"></i>
                          <strong>Funktionen bei aktivem MWI:</strong>
                          <ul className="mb-0 mt-2">
                            <li>Power-LED blinkt bei neuen Nachrichten</li>
                            <li>Popup-Benachrichtigung auf dem Display</li>
                            <li>Stotter-Freizeichen beim Abheben</li>
                            <li>Anzahl neuer Nachrichten auf Message-Taste</li>
                          </ul>
                        </div>
                      </Card.Body>
                    )}
                  </Card>
                </div>
              </Tab>

              <Tab eventKey="ldap" title="LDAP-Telefonbuch">
                <div className="pt-3">
                  <Card className="mb-3">
                    <Card.Header>
                      <Form.Check
                        type="switch"
                        label={<strong>LDAP-Telefonbuch aktivieren</strong>}
                        checked={templateForm.ldap_enabled}
                        onChange={e => setTemplateForm({ ...templateForm, ldap_enabled: e.target.checked })}
                      />
                    </Card.Header>
                    {templateForm.ldap_enabled && (
                      <Card.Body>
                        <p className="text-muted mb-3">
                          Das LDAP-Telefonbuch wird automatisch mit den Zugangsdaten der zugewiesenen Nebenstelle konfiguriert.
                        </p>
                        <Row>
                          <Col md={12}>
                            <Form.Check
                              type="switch"
                              label="Anrufer-Namen bei eingehenden Anrufen nachschlagen"
                              checked={templateForm.ldap_lookup_incoming}
                              onChange={e => setTemplateForm({ ...templateForm, ldap_lookup_incoming: e.target.checked })}
                              className="mb-2"
                            />
                            <Form.Text className="text-muted d-block mb-3 ms-4">
                              Zeigt den Namen des Anrufers aus dem Telefonbuch an
                            </Form.Text>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <Form.Check
                              type="switch"
                              label="Namen bei ausgehenden Anrufen nachschlagen"
                              checked={templateForm.ldap_lookup_outgoing}
                              onChange={e => setTemplateForm({ ...templateForm, ldap_lookup_outgoing: e.target.checked })}
                              className="mb-2"
                            />
                            <Form.Text className="text-muted d-block mb-3 ms-4">
                              Zeigt den Namen beim Wählen einer Nummer an
                            </Form.Text>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <Form.Check
                              type="switch"
                              label="Namen in der Anrufliste nachschlagen"
                              checked={templateForm.ldap_lookup_history}
                              onChange={e => setTemplateForm({ ...templateForm, ldap_lookup_history: e.target.checked })}
                              className="mb-2"
                            />
                            <Form.Text className="text-muted d-block ms-4">
                              Ergänzt Namen in der Anrufliste aus dem Telefonbuch
                            </Form.Text>
                          </Col>
                        </Row>
                      </Card.Body>
                    )}
                  </Card>
                </div>
              </Tab>

              <Tab eventKey="security" title="Sicherheit & Updates">
                <div className="pt-3">
                  <Card className="mb-3">
                    <Card.Header><i className="bi bi-shield-lock me-2"></i>Passwörter</Card.Header>
                    <Card.Body>
                      <Alert variant="info" className="mb-3">
                        <i className="bi bi-info-circle me-2"></i>
                        Wenn das Admin-Passwort auf "admin" (Standard) belassen wird, wird automatisch das im <strong>Mandanten</strong> hinterlegte Provisionierungs-Passwort verwendet.
                      </Alert>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Admin-Passwort</Form.Label>
                            <Form.Control
                              type="text"
                              value={templateForm.admin_password}
                              onChange={e => setTemplateForm({ ...templateForm, admin_password: e.target.value })}
                              placeholder="admin"
                            />
                            <Form.Text className="text-muted">
                              Passwort für das Telefon-Webinterface (Admin-Zugang)
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Benutzer-Passwort</Form.Label>
                            <Form.Control
                              type="text"
                              value={templateForm.user_password}
                              onChange={e => setTemplateForm({ ...templateForm, user_password: e.target.value })}
                              placeholder="user"
                            />
                            <Form.Text className="text-muted">
                              Passwort für das Telefon-Webinterface (Benutzer-Zugang)
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3">
                    <Card.Header><i className="bi bi-arrow-repeat me-2"></i>Automatische Re-Provisionierung</Card.Header>
                    <Card.Body>
                      <Form.Check
                        type="switch"
                        id="auto-provision-enabled"
                        label={<strong>Periodische Re-Provisionierung aktivieren</strong>}
                        checked={templateForm.auto_provision_enabled}
                        onChange={e => setTemplateForm({ ...templateForm, auto_provision_enabled: e.target.checked })}
                        className="mb-3"
                      />
                      {templateForm.auto_provision_enabled && (
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Intervall</Form.Label>
                              <Form.Select
                                value={templateForm.auto_provision_interval}
                                onChange={e => setTemplateForm({ ...templateForm, auto_provision_interval: parseInt(e.target.value) })}
                              >
                                <option value={60}>Stündlich (60 Min.)</option>
                                <option value={360}>Alle 6 Stunden</option>
                                <option value={720}>Alle 12 Stunden</option>
                                <option value={1440}>Täglich (24 Std.)</option>
                                <option value={10080}>Wöchentlich (7 Tage)</option>
                              </Form.Select>
                              <Form.Text className="text-muted">
                                Wie oft soll das Telefon die Konfiguration abrufen
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Feste Uhrzeit (optional)</Form.Label>
                              <Form.Control
                                type="time"
                                value={templateForm.auto_provision_time}
                                onChange={e => setTemplateForm({ ...templateForm, auto_provision_time: e.target.value })}
                              />
                              <Form.Text className="text-muted">
                                Wenn gesetzt, wird nur zu dieser Uhrzeit provisioniert (z.B. nachts)
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                      <Form.Text className="text-muted">
                        Bei aktivierter Re-Provisionierung lädt das Telefon regelmässig seine Konfiguration neu. Dies ermöglicht zentrale Änderungen ohne manuellen Neustart.
                      </Form.Text>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header><i className="bi bi-cloud-download me-2"></i>Firmware-Updates</Card.Header>
                    <Card.Body>
                      <Form.Check
                        type="switch"
                        id="auto-firmware-update"
                        label={<strong>Automatische Firmware-Updates aktivieren</strong>}
                        checked={templateForm.auto_firmware_update}
                        onChange={e => setTemplateForm({ ...templateForm, auto_firmware_update: e.target.checked })}
                      />
                      <Form.Text className="text-muted d-block mt-2">
                        Wenn aktiviert, prüft das Telefon bei jeder Provisionierung, ob eine neue Firmware-Version verfügbar ist und installiert diese automatisch.
                        <br />
                        <strong className="text-warning">Hinweis:</strong> Firmware-Updates sollten nur aktiviert werden, wenn getestet wurde, dass die Firmware mit allen Funktionen kompatibel ist.
                      </Form.Text>
                    </Card.Body>
                  </Card>
                </div>
              </Tab>

              <Tab eventKey="custom" title="Benutzerdefiniert">
                <div className="pt-3">
                  <Form.Group>
                    <Form.Label>Erweiterte Konfiguration</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={20}
                      value={templateForm.custom_config}
                      onChange={e => setTemplateForm({ ...templateForm, custom_config: e.target.value })}
                      className="font-monospace"
                      style={{ fontSize: '0.8rem' }}
                    />
                    <Form.Text className="text-muted">
                      Diese Parameter werden am Ende der Konfiguration hinzugefügt und können vorhandene Einstellungen überschreiben.
                    </Form.Text>
                  </Form.Group>
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">{editingTemplate ? 'Speichern' : 'Erstellen'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ASSIGNMENT MODAL */}
      <Modal show={showAssignmentModal} onHide={() => setShowAssignmentModal(false)} size={isSelectedTemplateDect() ? 'lg' : undefined}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingAssignment ? 'Zuweisung bearbeiten' : 'MAC-Adresse zuweisen'}
            {isSelectedTemplateDect() && <Badge bg="info" className="ms-2"><i className="bi bi-broadcast me-1"></i>DECT</Badge>}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAssignmentSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>MAC-Adresse</Form.Label>
              <InputGroup>
                <Form.Control
                  value={assignmentForm.mac_address}
                  onChange={e => setAssignmentForm({ ...assignmentForm, mac_address: e.target.value })}
                  required
                  placeholder="aa:bb:cc:dd:ee:ff"
                />
                <Button
                  variant="outline-primary"
                  onClick={() => setShowBarcodeScanner(true)}
                  title="Barcode scannen"
                >
                  <i className="bi bi-upc-scan"></i>
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Format: aa:bb:cc:dd:ee:ff oder aabbccddeeff -
                <span className="text-primary" style={{ cursor: 'pointer' }} onClick={() => setShowBarcodeScanner(true)}>
                  {' '}oder Yealink-Barcode scannen
                </span>
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Vorlage</Form.Label>
              <Form.Select
                value={assignmentForm.template_id}
                onChange={e => {
                  const newTemplateId = e.target.value
                  // Check if switching between DECT and non-DECT
                  const template = templates.find(t => t.id === newTemplateId)
                  const dectModels = ['W60B', 'W70B', 'W73B', 'W80B', 'W80DM', 'W90B', 'W90DM']
                  const isDect = template && dectModels.includes(template.model)
                  setAssignmentForm({
                    ...assignmentForm,
                    template_id: newTemplateId,
                    // Reset extension for DECT, keep dect_accounts for DECT
                    extension_id: isDect ? '' : assignmentForm.extension_id,
                    dect_accounts: isDect ? assignmentForm.dect_accounts : []
                  })
                }}
                required
              >
                {templates.map(t => {
                  const dectModels = ['W60B', 'W70B', 'W73B', 'W80B', 'W80DM', 'W90B', 'W90DM']
                  const isDect = dectModels.includes(t.model)
                  return (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.model}){isDect ? ' 📡' : ''}
                    </option>
                  )
                })}
              </Form.Select>
            </Form.Group>

            {/* Standard phone: single extension */}
            {!isSelectedTemplateDect() && (
              <Form.Group className="mb-3">
                <Form.Label>Nebenstelle (optional)</Form.Label>
                <Form.Select
                  value={assignmentForm.extension_id}
                  onChange={e => setAssignmentForm({ ...assignmentForm, extension_id: e.target.value })}
                >
                  <option value="">-- Keine Nebenstelle --</option>
                  {extensions.map(e => (
                    <option key={e.id} value={e.id}>{e.extension} - {e.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {/* DECT Base: multiple handsets */}
            {isSelectedTemplateDect() && (
              <Card className="mb-3 border-info">
                <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bi bi-broadcast me-2"></i>
                    DECT-Handsets ({assignmentForm.dect_accounts.length} / {getMaxHandsets()})
                  </span>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={addDectAccount}
                    disabled={assignmentForm.dect_accounts.length >= getMaxHandsets()}
                  >
                    <i className="bi bi-plus-lg me-1"></i>Handset hinzufügen
                  </Button>
                </Card.Header>
                <Card.Body>
                  {assignmentForm.dect_accounts.length === 0 ? (
                    <div className="text-center text-muted py-3">
                      <i className="bi bi-telephone" style={{ fontSize: '2rem' }}></i>
                      <p className="mb-0 mt-2">Noch keine Handsets konfiguriert</p>
                      <p className="small">Klicken Sie "Handset hinzufügen" um ein DECT-Handset anzulegen</p>
                    </div>
                  ) : (
                    <div>
                      {assignmentForm.dect_accounts.map((account, index) => (
                        <Card key={index} className="mb-2">
                          <Card.Body className="py-2">
                            <Row className="align-items-center">
                              <Col xs="auto">
                                <Badge bg="secondary">#{account.account}</Badge>
                              </Col>
                              <Col>
                                <Form.Control
                                  size="sm"
                                  placeholder="Handset-Name (z.B. Empfang, Lager...)"
                                  value={account.handset_name || ''}
                                  onChange={e => updateDectAccount(index, 'handset_name', e.target.value)}
                                />
                              </Col>
                              <Col>
                                <Form.Select
                                  size="sm"
                                  value={account.extension_id || ''}
                                  onChange={e => updateDectAccount(index, 'extension_id', e.target.value || null)}
                                >
                                  <option value="">-- Nebenstelle wählen --</option>
                                  {extensions.map(ext => (
                                    <option key={ext.id} value={ext.id}>
                                      {ext.extension} - {ext.name}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Col>
                              <Col xs="auto">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeDectAccount(index)}
                                  title="Handset entfernen"
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}
                  <Alert variant="info" className="mb-0 mt-2 small">
                    <i className="bi bi-info-circle me-2"></i>
                    Jedes Handset kann eine eigene Nebenstelle haben. Die DECT-Basisstation wird bei Yealink RPS mit dieser MAC registriert.
                  </Alert>
                </Card.Body>
              </Card>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung/Standort</Form.Label>
              <Form.Control
                value={assignmentForm.description}
                onChange={e => setAssignmentForm({ ...assignmentForm, description: e.target.value })}
                placeholder="z.B. Büro 101, Empfang..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAssignmentModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">{editingAssignment ? 'Speichern' : 'Zuweisen'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* CONFIG PREVIEW MODAL */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-code me-2"></i>
            Konfigurationsvorschau
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Row>
              <Col md={4}>
                <strong>MAC-Adresse:</strong> <code>{previewInfo.mac}</code>
              </Col>
              <Col md={4}>
                <strong>Vorlage:</strong> {previewInfo.template}
              </Col>
              <Col md={4}>
                <strong>Nebenstelle:</strong> {previewInfo.extension || '-'}
              </Col>
            </Row>
          </div>
          <div className="border rounded p-3 bg-dark text-light" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <pre className="mb-0" style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
              {previewConfig}
            </pre>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => {
              navigator.clipboard.writeText(previewConfig)
              toast.success('Konfiguration kopiert')
            }}
          >
            <i className="bi bi-clipboard me-1"></i> Kopieren
          </Button>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>Schließen</Button>
        </Modal.Footer>
      </Modal>

      {/* CLONE TEMPLATE MODAL */}
      <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-copy me-2"></i>
            Vorlage klonen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cloningTemplate && (
            <>
              <p className="text-muted">
                Erstellt eine Kopie der Vorlage <strong>"{cloningTemplate.name}"</strong> mit allen Einstellungen.
              </p>
              <Form.Group>
                <Form.Label>Name der neuen Vorlage</Form.Label>
                <Form.Control
                  type="text"
                  value={cloneName}
                  onChange={e => setCloneName(e.target.value)}
                  placeholder="Name eingeben..."
                  autoFocus
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloneModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleCloneTemplate} disabled={!cloneName.trim()}>
            <i className="bi bi-copy me-2"></i>
            Klonen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* RPS STATUS MODAL */}
      <Modal show={showRPSStatusModal} onHide={() => setShowRPSStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-cloud me-2"></i>
            Yealink RPS Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingRPSStatus ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Laden...</span>
              </div>
              <p className="mt-2 text-muted">Status wird bei Yealink abgefragt...</p>
            </div>
          ) : rpsStatusInfo ? (
            <>
              <div className="mb-3">
                <strong>MAC-Adresse:</strong>{' '}
                <code>{rpsStatusInfo.mac_address}</code>
              </div>

              <Card className="mb-3">
                <Card.Header>
                  <i className="bi bi-database me-2"></i>
                  Lokaler Status
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={4}><strong>Registriert:</strong></Col>
                    <Col sm={8}>
                      {rpsStatusInfo.local_registered ? (
                        <Badge bg="success">Ja</Badge>
                      ) : (
                        <Badge bg="secondary">Nein</Badge>
                      )}
                    </Col>
                  </Row>
                  {rpsStatusInfo.local_registered_at && (
                    <Row className="mt-2">
                      <Col sm={4}><strong>Registriert am:</strong></Col>
                      <Col sm={8}>{new Date(rpsStatusInfo.local_registered_at).toLocaleString('de-DE')}</Col>
                    </Row>
                  )}
                  {rpsStatusInfo.local_error && (
                    <Row className="mt-2">
                      <Col sm={4}><strong>Fehler:</strong></Col>
                      <Col sm={8}><span className="text-danger">{rpsStatusInfo.local_error}</span></Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <i className="bi bi-cloud me-2"></i>
                  Yealink RPS Status
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={4}><strong>Registriert:</strong></Col>
                    <Col sm={8}>
                      {rpsStatusInfo.yealink.registered ? (
                        <Badge bg="success">Ja</Badge>
                      ) : (
                        <Badge bg="warning">Nein</Badge>
                      )}
                    </Col>
                  </Row>
                  {rpsStatusInfo.yealink.source === 'disabled' && (
                    <Alert variant="info" className="mt-2 mb-0">
                      <i className="bi bi-info-circle me-2"></i>
                      {rpsStatusInfo.yealink.message}
                    </Alert>
                  )}
                  {rpsStatusInfo.yealink.error && (
                    <Alert variant="danger" className="mt-2 mb-0">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {rpsStatusInfo.yealink.error}
                    </Alert>
                  )}
                  {rpsStatusInfo.yealink.registered && rpsStatusInfo.yealink.server_url && (
                    <>
                      <Row className="mt-2">
                        <Col sm={4}><strong>Server-URL:</strong></Col>
                        <Col sm={8}><code>{rpsStatusInfo.yealink.server_url}</code></Col>
                      </Row>
                      {rpsStatusInfo.yealink.model && (
                        <Row className="mt-2">
                          <Col sm={4}><strong>Modell:</strong></Col>
                          <Col sm={8}>{rpsStatusInfo.yealink.model}</Col>
                        </Row>
                      )}
                      {rpsStatusInfo.yealink.created_at && (
                        <Row className="mt-2">
                          <Col sm={4}><strong>Erstellt:</strong></Col>
                          <Col sm={8}>{rpsStatusInfo.yealink.created_at}</Col>
                        </Row>
                      )}
                      {rpsStatusInfo.yealink.updated_at && (
                        <Row className="mt-2">
                          <Col sm={4}><strong>Aktualisiert:</strong></Col>
                          <Col sm={8}>{rpsStatusInfo.yealink.updated_at}</Col>
                        </Row>
                      )}
                    </>
                  )}
                  {!rpsStatusInfo.yealink.registered && rpsStatusInfo.yealink.source === 'yealink' && (
                    <Alert variant="warning" className="mt-2 mb-0">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Diese MAC-Adresse ist bei Yealink RPS nicht registriert.
                      {rpsStatusInfo.local_registered && (
                        <span className="d-block mt-1">
                          <strong>Hinweis:</strong> Lokaler Status zeigt "Registriert", aber Yealink kennt die MAC nicht mehr.
                          Klicken Sie "Erneut registrieren" um den Status zu synchronisieren.
                        </span>
                      )}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </>
          ) : (
            <Alert variant="secondary">
              Keine Status-Informationen verfügbar.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRPSStatusModal(false)}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* RPS-ABGLEICH / BEREINIGUNG */}
      <Modal show={showRpsFixModal} onHide={() => setShowRpsFixModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-wrench-adjustable me-2"></i>RPS abgleichen
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rpsFixChecking ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status" />
              <div className="mt-2 text-muted">Yealink-Konto wird abgefragt…</div>
            </div>
          ) : rpsFixResults ? (
            <>
              <p className="text-muted">Ergebnis der Bereinigung:</p>
              <Table size="sm" className="mb-0">
                <tbody>
                  {rpsFixResults.map(r => (
                    <tr key={`${r.action}-${r.mac}`}>
                      <td style={{width: '2rem'}}>
                        {r.success
                          ? <i className="bi bi-check-circle-fill text-success"></i>
                          : <i className="bi bi-x-circle-fill text-danger"></i>}
                      </td>
                      <td><code>{r.mac}</code></td>
                      <td>{r.action === 'register' ? 'Neu registriert' : 'Aus RPS-Konto entfernt'}</td>
                      <td className="text-danger small">{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {rpsFixResults.some(r => r.success && r.action === 'register') && (
                <Alert variant="info" className="mt-3 mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Die betroffenen Telefone holen die Redirect-URL erst beim nächsten Neustart.
                </Alert>
              )}
            </>
          ) : rpsProblemCount === 0 ? (
            <Alert variant="success" className="mb-0">
              <i className="bi bi-check-circle me-2"></i>
              Alles im Lot — jede Zuweisung liegt im Yealink-Konto und zeigt auf diesen Server,
              und es liegen keine Geräte ohne Zuweisung im Konto.
            </Alert>
          ) : (
            <>
              {rpsProblemMacs.length > 0 && (
                <>
                  <h6>Zuweisungen mit Abweichung</h6>
                  <p className="text-muted small">
                    Lokal als registriert geführt, im Yealink-Konto aber nicht vorhanden bzw. auf
                    einen anderen Server gebunden. Erneut registrieren bindet sie auf diesen Server.
                  </p>
                  <Table size="sm" className="mb-4">
                    <tbody>
                      {rpsProblemMacs.map(mac => {
                        const a = assignments.find(x => x.mac_address === mac)
                        const live = rpsLive[macKey(mac)]
                        return (
                          <tr key={mac}>
                            <td style={{width: '2rem'}}>
                              <Form.Check
                                checked={rpsFixSelection.macs.includes(mac)}
                                onChange={() => toggleFixMac(mac)}
                              />
                            </td>
                            <td><code>{mac}</code></td>
                            <td>{a?.extension ? `${a.extension.extension} - ${a.extension.name}` : (a?.description || '-')}</td>
                            <td>
                              {live?.state === 'missing'
                                ? <Badge bg="danger">nicht im Konto</Badge>
                                : <Badge bg="warning" text="dark">{live?.server_url || 'keine URL'}</Badge>}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </>
              )}

              {rpsOrphans.length > 0 && (
                <>
                  <h6>Geräte im RPS-Konto ohne Zuweisung</h6>
                  <p className="text-muted small">
                    Meist Karteileichen aus Tippfehlern oder gelöschten Zuweisungen. Sie werden aus
                    dem Yealink-Konto entfernt. Betrifft das ganze Konto, nicht nur einen Mandanten.
                  </p>
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {rpsOrphans.map(o => (
                        <tr key={o.mac}>
                          <td style={{width: '2rem'}}>
                            <Form.Check
                              checked={rpsFixSelection.orphans.includes(o.mac)}
                              onChange={() => toggleFixOrphan(o.mac)}
                            />
                          </td>
                          <td><code>{o.mac}</code></td>
                          <td className="text-muted small">{o.server_url || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              <Alert variant="warning" className="mt-3 mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Wirkt auf echte Telefone: sie fragen die Redirect-URL beim nächsten Neustart ab.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRpsFixModal(false)}>
            {rpsFixResults ? 'Schließen' : 'Abbrechen'}
          </Button>
          {!rpsFixResults && rpsProblemCount > 0 && (
            <Button
              variant="warning"
              onClick={runRpsFix}
              disabled={rpsFixRunning || rpsFixChecking || (rpsFixSelection.macs.length + rpsFixSelection.orphans.length) === 0}
            >
              {rpsFixRunning
                ? <><span className="spinner-border spinner-border-sm me-2" />Bereinige…</>
                : <><i className="bi bi-wrench-adjustable me-1"></i>{rpsFixSelection.macs.length + rpsFixSelection.orphans.length} Einträge bereinigen</>}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* BARCODE SCANNER */}
      <BarcodeScanner
        show={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onScan={(mac) => {
          setAssignmentForm({ ...assignmentForm, mac_address: mac })
          toast.success(`MAC-Adresse ${mac} eingelesen`)
        }}
      />
    </div>
  )
}
